<template>
    <basic-header pretitle="Aqfer graph management" title="Graph Curation" />

    <div class="container-fluid graph-curation-page mt-3 mb-4">
        <curation-tabs />
        <template v-if="!curationUnavailable">

        <!-- ── Unified Topbar ──────────────────────────────────────────── -->
        <div class="card border-0 shadow-sm curation-topbar-card">
            <div class="card-body curation-topbar-body">
                <div class="curation-topbar-inner">
                    <!-- Controls: Dataset | Records | Range | Load -->
                    <div class="d-flex align-items-center gap-2 gap-lg-3 min-w-0 curation-controls-group">
                        <!-- Dataset -->
                        <div
                            v-bs-tooltip="tips.dataset"
                            class="d-flex align-items-center gap-2 flex-shrink-0"
                        >
                            <span class="curation-inline-label">Dataset <i class="fa fa-circle-info curation-help-icon" /></span>
                            <div class="btn-group btn-group-sm dataset-toggle" role="group">
                                <button
                                    v-if="datasetAvailability.merge"
                                    type="button"
                                    class="btn"
                                    :class="dataset === 'merge' ? 'btn-primary' : 'btn-outline-primary'"
                                    @click="setDataset('merge')"
                                >Merge</button>
                                <button
                                    v-if="datasetAvailability.split"
                                    type="button"
                                    class="btn"
                                    :class="dataset === 'split' ? 'btn-primary' : 'btn-outline-primary'"
                                    @click="setDataset('split')"
                                >Split</button>
                            </div>
                        </div>

                        <div class="curation-v-sep"></div>

                        <!-- Records -->
                        <!-- On the whole group, label included: on the input alone, hovering the
                             label showed nothing. -->
                        <div v-bs-tooltip="tips.records" class="d-flex align-items-center gap-2 flex-shrink-0">
                            <span class="curation-inline-label">Records <i class="fa fa-circle-info curation-help-icon" /></span>
                            <input
                                v-model.number="recordCountInput"
                                type="number"
                                class="form-control form-control-sm curation-records-input"
                                min="1"
                                step="1"
                            />
                        </div>

                        <!-- Range -->
                        <div
                            class="d-flex align-items-center gap-2 flex-shrink-0"
                            title="Which suggestions in the ranked list you are viewing."
                        >
                            <span class="curation-inline-label">Range</span>
                            <span v-if="offsetLoading" class="spinner-border spinner-border-sm text-secondary" role="status" aria-label="Loading offset…" />
                            <span v-else-if="loadRangeLabel" class="font-monospace small fw-semibold curation-range-value">{{ loadRangeLabel }}</span>
                            <span v-else class="small text-muted">—</span>
                        </div>

                        <!-- Load button -->
                        <!-- Tooltip on the wrapper: a disabled button gets no hover of its own. -->
                        <div v-bs-tooltip="tips.load" class="ms-auto flex-shrink-0">
                            <button
                                type="button"
                                class="btn btn-primary btn-sm px-2 px-lg-3 text-nowrap"
                                :disabled="listLoading || !canLoadList"
                                @click="loadRecommendations"
                            >
                                <span v-if="listLoading" class="spinner-border spinner-border-sm me-1" role="status" />
                                <i v-else class="fa fa-list me-1" />
                                <span class="d-none d-md-inline">Load recommendations</span><span class="d-md-none">Load</span>
                            </button>
                        </div>
                    </div>

                    <!-- Vertical separator between controls and stats (xl+) -->
                    <div class="curation-topbar-sep"></div>

                    <!-- Stats + Submit -->
                    <div class="d-flex align-items-center gap-2 gap-lg-3 flex-shrink-0 curation-stats-group">
                        <div v-bs-tooltip="tips.rows" class="curation-stat-chip">
                            <span class="curation-stat-chip__label">Rows loaded <i class="fa fa-circle-info curation-help-icon" /></span>
                            <span class="curation-stat-chip__value text-primary">{{ recommendations.length }}</span>
                        </div>
                        <div class="curation-v-sep"></div>
                        <div
                            class="curation-stat-chip"
                            title="How many of the loaded suggestions you have accepted or rejected."
                        >
                            <span class="curation-stat-chip__label">Decisions</span>
                            <span class="curation-stat-chip__value curation-stat-chip__value--decisions">{{ decisionsProgressLabel }}</span>
                            <span
                                v-if="skippedRecommendationCount"
                                class="curation-stat-chip__skipped"
                                :title="`${skippedRecommendationCount} undecided above your last decision will be skipped on save`"
                            >{{ skippedRecommendationCount }} skipped</span>
                        </div>
                        <span
                            v-if="!canCurate"
                            class="badge bg-secondary-soft text-secondary curation-view-only"
                            title="Your role can view recommendations but not save decisions"
                        ><i class="fa fa-eye me-1" />View only</span>
                        <!-- Wrapper carries the tooltip: Save starts disabled, and a disabled button
                             never showed its help. -->
                        <span v-else v-bs-tooltip="saveDecisionsTitle" class="d-inline-block" tabindex="0">
                            <button
                                type="button"
                                class="btn btn-success btn-sm btn-submit-review text-nowrap"
                                :disabled="submitting || !canSaveDecisions"
                                @click="submitDecisions"
                            >
                                <span v-if="submitting" class="spinner-border spinner-border-sm me-1" role="status" />
                                <i v-else class="fa fa-cloud-arrow-up me-1" />
                                Save Decisions
                            </button>
                        </span>
                    </div>
                </div>
                <div v-if="offsetError" class="alert alert-warning mt-2 mb-0 py-2 small">{{ offsetError }}</div>
                <div
                    v-else-if="currentOffset != null && (!Number.isFinite(Number(recordCountInput)) || Number(recordCountInput) < 1)"
                    class="alert alert-info mt-2 mb-0 py-2 small"
                >
                    Enter at least 1 record to load (default 10).
                </div>
            </div>
        </div>

        <div
            class="row g-2 mb-0 align-items-stretch curation-bench-row"
            :style="{ '--curation-bench-min-h': graphAreaMinHeight + 'px' }"
        >
            <div class="col-12 col-lg-4 curation-bench-col d-flex">
                <div class="card border-0 shadow-sm recommendations-card w-100 d-flex flex-column">
                    <div
                        class="card-header bg-transparent border-0 d-flex align-items-center justify-content-between recommendations-card-header"
                    >
                        <!-- Saving moves a forward-only cursor past the last decided row, so the
                             order matters to the curator; the explanation is on demand. -->
                        <h5 class="card-title mb-0">
                            Recommendations
                            <i v-bs-tooltip="tips.order" class="fa fa-circle-info curation-help-icon" tabindex="0" />
                        </h5>
                        <span v-if="listPartial" class="badge bg-warning text-dark">Partial result</span>
                    </div>
                    <div class="card-body d-flex flex-column flex-grow-1 curation-list-body">
                        <div v-if="listError" class="alert alert-danger small">{{ listError }}</div>
                        <div v-else-if="listLoading && !recommendations.length" class="text-center py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading recommendations…</span>
                            </div>
                            <p class="text-muted small mt-2 mb-0">Loading recommendations…</p>
                        </div>
                        <div v-else-if="!recommendations.length" class="text-muted text-center py-5">
                            <i class="fa fa-diagram-project fa-2x mb-3 d-block opacity-50" />
                            Click
                            <strong>Load recommendations</strong> to fetch rows.
                        </div>
                        <template v-else>
                            <div class="d-flex flex-wrap align-items-center gap-2 mb-2 curation-bulk-bar">
                                <span class="small text-muted me-auto selected-count">{{ selectionCount }} selected</span>
                                <button
                                    type="button"
                                    class="btn btn-outline-success btn-sm"
                                    :disabled="!canCurate || !selectionCount"
                                    @click="bulkSetDecision('accept')"
                                >
                                    Accept selected
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-outline-danger btn-sm"
                                    :disabled="!canCurate || !selectionCount"
                                    @click="bulkSetDecision('reject')"
                                >
                                    Reject selected
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-link btn-sm text-secondary py-0"
                                    :disabled="!selectionCount"
                                    @click="clearRecommendationSelection"
                                >
                                    Clear selection
                                </button>
                            </div>
                            <div class="curation-table-wrap">
                            <table class="table table-hover table-sm align-middle mb-0 curation-data-table">
                                <colgroup v-if="dataset === 'merge'">
                                    <col class="curation-col-w-check" />
                                    <col class="curation-col-w-recid" />
                                    <col />
                                    <col />
                                    <col class="curation-col-w-actions" />
                                </colgroup>
                                <colgroup v-else>
                                    <col class="curation-col-w-check" />
                                    <col class="curation-col-w-recid" />
                                    <col />
                                    <col class="curation-col-w-actions" />
                                </colgroup>
                                <thead class="table-light">
                                    <tr>
                                        <th scope="col" class="curation-th-checkbox text-center">
                                            <input
                                                ref="selectAllCheckbox"
                                                type="checkbox"
                                                class="form-check-input m-0"
                                                title="Select all rows"
                                                aria-label="Select all rows"
                                                @change="onSelectAllChange"
                                            />
                                        </th>
                                        <th scope="col" class="text-muted small text-uppercase curation-col-recid">Rec ID</th>
                                        <template v-if="dataset === 'merge'">
                                            <th scope="col" class="text-muted small text-uppercase curation-col-cluster">Cluster 1</th>
                                            <th scope="col" class="text-muted small text-uppercase curation-col-cluster">Cluster 2</th>
                                        </template>
                                        <template v-else>
                                            <th scope="col" class="text-muted small text-uppercase curation-col-cluster">Cluster</th>
                                        </template>
                                        <th scope="col" class="text-muted small text-uppercase curation-col-actions-h">Decision</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="(row, idx) in recommendations"
                                        :key="rowKey(row, idx)"
                                        :class="{ 'table-active': selectedRowIndex === idx }"
                                        class="curation-row"
                                        @click="selectRow(row, idx)"
                                    >
                                        <td class="text-center curation-td-checkbox" @click.stop>
                                            <input
                                                type="checkbox"
                                                class="form-check-input m-0"
                                                :checked="isRowBulkChecked(row, idx)"
                                                :aria-label="'Select row ' + (idx + 1)"
                                                @change="setRowBulkChecked(row, idx, $event.target.checked)"
                                            />
                                        </td>
                                        <td class="font-monospace small curation-col-recid">{{ row.recommendation_id ?? '—' }}</td>
                                        <template v-if="dataset === 'merge'">
                                            <td class="font-monospace small curation-col-cluster">
                                                <button
                                                    type="button"
                                                    class="cluster-link-btn cluster-link-btn--cell"
                                                    :class="{ 'is-anchor-active': selectedRowIndex === idx && mergeAnchorHighlightsC1 }"
                                                    :title="row.cluster_id1"
                                                    @click.stop="selectRowAndFocus(row, idx, 'c1')"
                                                >
                                                    {{ row.cluster_id1 }}
                                                </button>
                                            </td>
                                            <td class="font-monospace small curation-col-cluster">
                                                <button
                                                    type="button"
                                                    class="cluster-link-btn cluster-link-btn--cell"
                                                    :class="{ 'is-anchor-active': selectedRowIndex === idx && mergeAnchorHighlightsC2 }"
                                                    :title="row.cluster_id2"
                                                    @click.stop="selectRowAndFocus(row, idx, 'c2')"
                                                >
                                                    {{ row.cluster_id2 }}
                                                </button>
                                            </td>
                                        </template>
                                        <template v-else>
                                            <td class="font-monospace small curation-col-cluster">
                                                <button
                                                    type="button"
                                                    class="cluster-link-btn cluster-link-btn--cell"
                                                    :class="{ 'is-anchor-active': selectedRowIndex === idx }"
                                                    :title="splitRowClusterId(row)"
                                                    @click.stop="selectRowAndFocus(row, idx, 'c1')"
                                                >
                                                    {{ splitRowClusterId(row) }}
                                                </button>
                                            </td>
                                        </template>
                                        <td class="curation-col-actions" @click.stop>
                                            <div class="curation-action-btns d-flex gap-1 align-items-center justify-content-center">
                                                <button
                                                    type="button"
                                                    class="btn btn-sm curation-decision-btn"
                                                    :class="decisionClass(row, idx, 'accept')"
                                                    :disabled="!canCurate"
                                                    :title="canCurate ? 'Accept' : 'View only'"
                                                    aria-label="Accept"
                                                    @click="setDecision(row, idx, 'accept')"
                                                >
                                                    <i class="fa fa-check" />
                                                </button>
                                                <button
                                                    type="button"
                                                    class="btn btn-sm curation-decision-btn"
                                                    :class="decisionClass(row, idx, 'reject')"
                                                    :disabled="!canCurate"
                                                    :title="canCurate ? 'Reject' : 'View only'"
                                                    aria-label="Reject"
                                                    @click="setDecision(row, idx, 'reject')"
                                                >
                                                    <i class="fa fa-xmark" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <div class="col-12 col-lg-8 curation-bench-col d-flex">
                <div class="card border-0 shadow-sm graph-detail-card w-100 d-flex flex-column">
                    <div
                        class="card-header bg-transparent border-0 d-flex flex-wrap align-items-center gap-2 graph-detail-card-header"
                    >
                        <h5 class="card-title mb-0 me-auto">Cluster graph</h5>
                        <!-- Split mode: add new cluster button -->
                        <template v-if="canCurate && dataset === 'split' && selectedRow && detailGraph">
                            <button
                                type="button"
                                class="btn btn-outline-secondary btn-sm flex-shrink-0"
                                title="Create a new split cluster; drag nodes from the origin cluster onto it"
                                @click="createSplitCluster"
                            >
                                <i class="fa fa-plus me-1" />
                                Add cluster
                            </button>
                        </template>
                        <template v-if="dataset === 'merge' && selectedRow">
                            <div class="d-flex align-items-center gap-2 flex-shrink-0 flex-wrap">
                                <!-- One click for every connected id, instead of opening each node
                                     in turn. Per-group merge stays the precise route. -->
                                <button
                                    v-if="canCurate && bulkMergeGroups.length"
                                    type="button"
                                    class="btn btn-sm flex-shrink-0"
                                    :class="bulkMergeStaged ? 'btn-warning' : 'btn-outline-warning'"
                                    :title="bulkMergeTitle"
                                    @click="toggleBulkMerge"
                                >
                                    <i class="fa fa-link me-1" />
                                    <template v-if="bulkMergeStaged">Undo merge all</template>
                                    <template v-else>
                                        Merge {{ bulkMergeIdCount }} connected
                                        <span class="bulk-merge-dir">
                                            → cluster {{ bulkMergeDirection === 'c2' ? 1 : 2 }}
                                        </span>
                                    </template>
                                </button>
                                <div class="cluster-anchor-row cluster-anchor-row--header btn-group btn-group-sm" role="group" aria-label="Cluster scope">
                                    <button type="button" class="btn btn-anchor" :class="detailFocus === 'both' ? 'btn-anchor-active' : 'btn-anchor-idle'" title="Show both clusters side by side." @click="setDetailFocus('both')">Both</button>
                                    <button type="button" class="btn btn-anchor btn-anchor-c1" :class="detailFocus === 'c1' ? 'btn-anchor-active' : 'btn-anchor-idle'" :title="`Show only Cluster 1${selectedRow ? ' \u00b7 ' + (selectedRow.cluster_id1 || selectedRow.cluster_id) : ''}`" @click="setDetailFocus('c1')">
                                        Cluster 1
                                        <span v-if="selectedRow" class="cluster-btn-id font-monospace">{{ shortClusterId(selectedRow.cluster_id1 || selectedRow.cluster_id) }}</span>
                                    </button>
                                    <button type="button" class="btn btn-anchor btn-anchor-c2" :class="detailFocus === 'c2' ? 'btn-anchor-active' : 'btn-anchor-idle'" :title="`Show only Cluster 2${selectedRow?.cluster_id2 ? ' \u00b7 ' + selectedRow.cluster_id2 : ''}`" @click="setDetailFocus('c2')">
                                        Cluster 2
                                        <span v-if="selectedRow?.cluster_id2" class="cluster-btn-id font-monospace">{{ shortClusterId(selectedRow.cluster_id2) }}</span>
                                    </button>
                                </div>
                                <!-- Preview merged: only shown when no manual moves have been made yet -->
                                <div
                                    v-if="mergeNodeMoves.length === 0"
                                    class="form-check form-check-inline mb-0 small"
                                    title="Shows how the two clusters would look combined into one. Turns off once you move a node."
                                >
                                    <input id="curation-merge-preview" v-model="mergePreviewEnabled" class="form-check-input" type="checkbox" />
                                    <label class="form-check-label text-muted" for="curation-merge-preview">Preview merged</label>
                                </div>
                            </div>
                        </template>
                    </div>
                    <div class="card-body graph-detail-card-body flex-grow-1 d-flex flex-column">
                        <div v-if="detailError" class="alert alert-danger small">{{ detailError }}</div>
                        <div v-else-if="detailLoading" class="text-center py-5">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading graph…</span>
                            </div>
                        </div>
                        <div v-else-if="!detailGraph && !selectedRow" class="text-muted text-center py-5">
                            <i class="fa fa-circle-nodes fa-2x mb-3 d-block opacity-50" />
                            Select a row from the recommendations list to view its cluster graph.
                        </div>
                        <div v-else-if="!detailGraph" class="text-muted text-center py-5">
                            <i class="fa fa-triangle-exclamation fa-2x mb-3 d-block opacity-50" />
                            No graph data available for this recommendation.
                        </div>
                        <div v-else class="graph-detail-inner">
                            <div v-if="nodeMoveEnabled && !hintDismissed" class="node-move-hint d-flex align-items-center gap-2 mb-1">
                                <i class="fa fa-hand-pointer text-muted" style="font-size:0.75rem"></i>
                                <span class="small text-muted text-truncate">
                                    <template v-if="dataset === 'split'">
                                        {{ splitNewClusters.length ? 'Drag nodes onto a new cluster hub to move them.' : 'Click Add cluster above, then drag nodes onto it.' }}
                                    </template>
                                    <template v-else-if="moveSelectionNode">
                                        <strong class="text-warning">{{ moveSelectionNode.node.neighborType || moveSelectionNode.node.key }}</strong>
                                        armed — toggle IDs, then drag to the target cluster.
                                    </template>
                                    <template v-else-if="clusterEdgeCount">
                                        <strong>Click</strong> a group to see its connections to the other cluster, then hover an ID and click <strong>Move</strong> to send it to the other cluster. Nodes can also be dragged between clusters.
                                    </template>
                                    <template v-else>
                                        <strong>Click</strong> a group to choose IDs, then <strong>drag</strong> to the other cluster. Single nodes drag directly.
                                    </template>
                                </span>
                                <button
                                    type="button"
                                    class="btn-close btn-close-sm ms-auto"
                                    style="font-size: 0.6rem"
                                    title="Hide this hint"
                                    @click="dismissHint"
                                ></button>
                            </div>
                            <!-- One compact toolbar row: ID filter plus, for merge, the score and
                                 the connecting-id summary. These were three stacked bars, which
                                 cost ~140px of canvas height before the graph even started. The
                                 long explanation now lives in the chip's tooltip. -->
                            <div v-if="detailGraph" class="cluster-graph-toolbar mb-2 d-flex flex-wrap align-items-center gap-2">
                                <label class="small text-muted mb-0" for="curation-graph-id-filter">Filter by ID</label>
                                <input
                                    id="curation-graph-id-filter"
                                    v-model.trim="curationGraphIdFilterInput"
                                    type="text"
                                    class="form-control form-control-sm curation-graph-id-filter-input"
                                    placeholder="Substring matches entity ids in both clusters"
                                    autocomplete="off"
                                    @blur="flushCurationIdFilter"
                                    @keydown.enter.prevent="flushCurationIdFilter"
                                />
                                <template v-if="dataset === 'merge'">
                                    <span
                                        v-if="recommendationScore != null"
                                        class="badge bg-primary-soft"
                                        title="Confidence score for this suggestion. Higher means the algorithm is more sure."
                                    >
                                        Score {{ recommendationScore }}
                                    </span>
                                    <!-- Plain stat, not a control: the lines appear when a group is
                                         clicked on the graph, which the tooltip says. -->
                                    <span
                                        v-if="clusterEdgeCount"
                                        class="cluster-edges-chip"
                                        :title="clusterEdgeHelpText"
                                    >
                                        <svg class="chip-line-swatch" width="22" height="8" aria-hidden="true">
                                            <line
                                                x1="1" y1="4" x2="21" y2="4"
                                                stroke="currentColor" stroke-width="1.8"
                                                stroke-dasharray="0.1 4.5" stroke-linecap="round"
                                            />
                                        </svg>
                                        {{ clusterEdgeCount }}
                                        {{ clusterEdgeCount === 1 ? 'connection' : 'connections' }}
                                    </span>
                                </template>
                            </div>
                            <div v-if="canLoadMoreOntoGraph" class="cluster-graph-pager mb-2 d-flex flex-wrap align-items-center gap-2">
                                <button type="button" class="btn btn-sm btn-outline-primary" @click="loadMoreGraphEntitiesOnGraph">
                                    Load next {{ graphEntityStep }} entities on graph
                                </button>
                                <span class="small text-muted">
                                    Showing {{ entityGraphCap }} per hub with flat lists (max {{ graphEntityCapMax }}).
                                </span>
                            </div>

                            <!-- D3 graph + floating overlay share this relative container -->
                            <div class="graph-canvas-wrap">
                                <D3CurationGraph
                                    :data="enhancedDisplayGraph"
                                    :node-colors="graphNodeColors"
                                    :node-labels="graphNodeLabels"
                                    :show-bridge-stats="dataset === 'merge' && !mergePreviewEnabled"
                                    :cluster-edge-count="dataset === 'merge' ? clusterEdgeCount : 0"
                                    :enable-node-move="nodeMoveEnabled"
                                    :highlight-node-id="moveSelectionNode ? moveSelectionNode.nodeId : null"
                                    :merged-entity-ids="mergedEntityIdsByNode"
                                    :flash="mergeFlash"
                                    @node-moved="handleGraphNodeMoved"
                                    @group-node-clicked="handleGroupNodeClicked"
                                    @merge-connection="mergeConnectionGroup"
                                    @select-ids-to-move="handleGroupNodeClicked"
                                />

                                <!-- Floating ID-selection panel — click-triggered, stays open until drop or dismiss -->
                                <transition name="drag-sel">
                                    <div v-if="moveSelectionNode" class="drag-sel-panel">

                                        <!-- Header: type label + bulk actions -->
                                        <div class="dsp-header">
                                            <span class="dsp-step-badge">1</span>
                                            <i class="fa fa-layer-group dsp-header-icon"></i>
                                            <span class="dsp-type" :title="moveSelectionNode.node.neighborType || moveSelectionNode.node.key">
                                                {{ moveSelectionNode.node.neighborType || moveSelectionNode.node.key }}
                                            </span>
                                            <div class="dsp-actions">
                                                <button type="button" class="dsp-action-btn" @click="selectAllMoveIds" title="Select all IDs">
                                                    <i class="fa fa-check-double"></i> All
                                                </button>
                                                <button type="button" class="dsp-action-btn dsp-action-btn--clear" @click="deselectAllMoveIds" title="Clear selection">
                                                    <i class="fa fa-circle-minus"></i> None
                                                </button>
                                                <button type="button" class="dsp-close-btn" @click="cancelMoveSelection" title="Close panel">
                                                    <i class="fa fa-xmark"></i>
                                                </button>
                                            </div>
                                        </div>

                                        <!-- Animated selection progress bar -->
                                        <div class="dsp-progress-track">
                                            <div class="dsp-progress-fill" :style="{ width: totalMoveIds ? (selectedMoveIdCount / totalMoveIds * 100) + '%' : '0%' }"></div>
                                        </div>

                                        <!-- Count subline -->
                                        <div class="dsp-subline">
                                            <span class="dsp-sel-count" :class="{ 'dsp-sel-count--warn': selectedMoveIdCount === 0 }">{{ selectedMoveIdCount }}</span>
                                            <span class="dsp-sel-sep">of</span>
                                            <span class="dsp-sel-total">{{ totalMoveIds }}</span>
                                            IDs will migrate &mdash; click to toggle
                                        </div>

                                        <!-- ID list — full-width rows, sorted: selected first -->
                                        <div class="dsp-chips-area">
                                            <button
                                                v-for="[eid, checked] in sortedMoveSelectionEntries"
                                                :key="eid"
                                                type="button"
                                                class="dsp-chip"
                                                :class="{ 'dsp-chip--on': checked, 'dsp-chip--off': !checked }"
                                                :title="(checked ? 'Will migrate: ' : 'Stays in place: ') + eid"
                                                @click="toggleMoveSelectionId(eid)"
                                            >
                                                <i class="dsp-chip-icon" :class="checked ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'"></i>
                                                <span class="dsp-chip-text">{{ eid }}</span>
                                            </button>
                                        </div>

                                        <!-- Footer: step 2 instruction -->
                                        <div class="dsp-footer">
                                            <div class="dsp-step2-row">
                                                <span class="dsp-step-badge dsp-step-badge--2">2</span>
                                                <template v-if="selectedMoveIdCount === 0">
                                                    <i class="fa fa-triangle-exclamation dsp-warn-icon"></i>
                                                    <span class="dsp-footer-warn">Select at least one ID first</span>
                                                </template>
                                                <template v-else>
                                                    <i class="fa fa-up-down-left-right dsp-hint-icon"></i>
                                                    <span class="dsp-footer-hint">Drag the <strong>glowing node</strong> to a cluster</span>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── Node Movement Log ────────────────────────────────────────────
             Inside the same container-fluid, BELOW the bench row.
             The bench row keeps its fixed height; this section makes the
             page scroll naturally so users can always read the full log.
        ─────────────────────────────────────────────────────────────────── -->
        <div v-if="hasNodeMoves" class="node-move-section mb-5">
            <div class="card border-0 shadow-sm node-move-card">
                <!-- Header -->
                <div class="card-header node-move-card-header d-flex align-items-center gap-2 flex-wrap">
                    <i class="fa fa-shuffle text-primary me-1"></i>
                    <span class="node-move-log-title">Node Movements</span>
                    <span v-if="totalMovesCount" class="badge bg-primary rounded-pill">{{ totalMovesCount }}</span>
                    <span class="small text-muted ms-1">
                        {{ dataset === 'merge' ? 'Nodes reassigned between the two clusters' : 'Nodes moved to new split clusters' }}
                    </span>
                    <button
                        type="button"
                        class="btn btn-outline-danger btn-sm node-move-clear-btn ms-auto"
                        title="Undo every node move in this recommendation and return to the original graph."
                        @click="resetNodeMoveState"
                    >
                        <i class="fa fa-rotate-left me-1"></i>Clear all
                    </button>
                </div>

                <!-- Merge mode: one row per type~domain~entityId with individual remove -->
                <div v-if="dataset === 'merge' && mergeNodeMoves.length" class="node-move-table-scroll">
                    <table class="table table-sm table-hover align-middle node-move-table mb-0">
                        <thead class="table-light">
                            <tr>
                                <th class="small text-muted text-uppercase ps-3">type~domain~entityId</th>
                                <th class="small text-muted text-uppercase">From</th>
                                <th class="small text-muted text-uppercase text-center" style="width:1.5rem">→</th>
                                <th class="small text-muted text-uppercase">To</th>
                                <th class="small text-muted text-uppercase" style="width:2.5rem">
                                    <span class="visually-hidden">Remove</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="mv in mergeNodeMoves" :key="mv.nodeId + mv.nodeLabel">
                                <td class="font-monospace small ps-3">
                                    <span class="node-move-id-text" :title="mv.nodeLabel">{{ mv.nodeLabel }}</span>
                                </td>
                                <td class="small">
                                    <span class="node-move-cluster-chip node-move-cluster-chip--from">{{ mv.fromLabel }}</span>
                                </td>
                                <td class="text-center small text-muted">→</td>
                                <td class="small">
                                    <span class="node-move-cluster-chip node-move-cluster-chip--to">{{ mv.toLabel }}</span>
                                </td>
                                <td class="text-center">
                                    <button
                                        type="button"
                                        class="btn-remove-move"
                                        title="Keep this ID in its original cluster (remove from migration)"
                                        @click="removeMergeMove(mv.nodeId, mv.nodeLabel)"
                                    >
                                        <i class="fa fa-xmark"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Split mode: per-cluster sections with removable chips -->
                <div v-else-if="dataset === 'split'" class="card-body node-move-split-body mb-2">
                    <div
                        v-for="cluster in splitNewClusters"
                        :key="cluster.id"
                        class="split-cluster-section"
                    >
                        <div class="split-cluster-section-header">
                            <i class="fa fa-circle-nodes me-1 opacity-75"></i>
                            <span class="split-cluster-log-label">{{ cluster.label }}</span>
                            <span class="badge bg-primary rounded-pill ms-1">
                                {{ (splitNodeMovesByCluster[cluster.id] || []).length }}
                            </span>
                            <code class="split-cluster-id ms-2" :title="cluster.clusterId">{{ cluster.clusterId.slice(0, 20) + '...' }}</code>
                        </div>
                        <div v-if="(splitNodeMovesByCluster[cluster.id] || []).length" class="split-cluster-nodes mt-2">
                            <span
                                v-for="mv in (splitNodeMovesByCluster[cluster.id] || [])"
                                :key="mv.nodeLabel"
                                class="split-node-chip-wrap"
                            >
                                <span class="split-node-chip-label" :title="mv.nodeLabel">{{ mv.nodeLabel }}</span>
                                <button
                                    type="button"
                                    class="split-chip-remove"
                                    title="Keep this ID in origin cluster"
                                    @click="removeSplitMove(mv.nodeId, mv.nodeLabel)"
                                >
                                    <i class="fa fa-xmark"></i>
                                </button>
                            </span>
                        </div>
                        <div v-else class="small text-muted fst-italic mt-1">
                            No IDs moved yet — drag entity nodes here
                        </div>
                    </div>
                    <div v-if="!splitNewClusters.length" class="text-muted small fst-italic">
                        Click <strong>Add cluster</strong> in the graph header, then drag nodes onto the new hub.
                    </div>
                </div>
            </div>
        </div>
        </template>
        <!-- Neither merge nor split has a recommendation table for this client (config 404s). -->
        <div v-else class="card border-0 shadow-sm">
            <div class="card-body text-center text-muted py-5">
                <i class="fa fa-diagram-project fa-2x mb-3 d-block opacity-50" />
                No curation recommendations are available for this client.
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { defineComponent, h } from 'vue'
import BasicHeader from '../../components/BasicHeader.vue'
import CurationTabs from '../../components/CurationTabs.vue'
import { getCurationAvailability } from '../../utils/curationAvailability'
import { bsTooltip } from '../../utils/bsTooltip'
import D3CurationGraph from '../../components/D3CurationGraph.vue'

const MAX_POLL = 60
const POLL_MS = 1000
const DEFAULT_SPLIT_INTO = 2
/** Flat entity_list*: initial dots per hub; user can raise with “Load next on graph” up to CURATION_GRAPH_ENTITY_CAP_MAX. */
const CURATION_GRAPH_FLAT_ENTITY_CAP = 80
const CURATION_GRAPH_ENTITY_STEP = 80
const CURATION_GRAPH_ENTITY_CAP_MAX = 400
const CURATION_BUNDLE_ID_PREVIEW = 250
/**
 * Bucket key for group members that `connecting_cluster_edges` gives no edge for. Not a valid
 * type~domain signature, so it can never collide with a real one.
 */
const RELATION_NONE = '\u0000none'
/**
 * Cluster-membership link kinds — 'c1', 'c2', 'c3'… A membership edge means "this node is in
 * that cluster", so it is what cluster counts, drag-and-drop origin and layout lanes read.
 * Every other kind ('connects') is annotation and must be excluded from those.
 */
const isMembershipLinkKind = (kind) => /^c\d+$/.test(String(kind || ''))

/**
 * `entity_list` in the curation payload is keyed by `entity_type~entity_domain`. A flat id list
 * (legacy/mock shape) has no such key, so it collapses under this one. The real
 * pairs1_final_cluster_output always returns entity_type/entity_domain groups.
 */
const CURATION_FLAT_PAIR_LABEL = 'entity~default'

const HINT_DISMISSED_KEY = 'graphCuration.hintDismissed'
/* Storage can throw (private mode, blocked site data); the hint then simply shows again. */
const readHintDismissed = () => {
    try { return localStorage.getItem(HINT_DISMISSED_KEY) === '1' } catch (e) { return false }
}
const moveKey = (m) => `${m.nodeId}::${m.entityId}`
/* Ids differ at the tail; the last 12 characters identify one in a toast. */
const shortId = (id) => {
    const s = String(id)
    return s.length > 14 ? `\u2026${s.slice(-12)}` : s
}

/* Toast body with an explicit Undo button. Clicking the toast itself only closes it, so an
   undo is never triggered by accident and is always confirmed by its own toast. */
const UndoToast = defineComponent({
    props: { text: { type: String, required: true } },
    emits: ['undo', 'close-toast'],
    render() {
        return h('div', { class: 'curation-undo-toast' }, [
            h('span', this.text),
            h('button', {
                type: 'button',
                class: 'btn btn-sm btn-light curation-undo-toast__btn',
                onClick: (e) => {
                    e.stopPropagation()
                    this.$emit('undo')
                    this.$emit('close-toast')
                },
            }, 'Undo'),
        ])
    },
})

export default {
    directives: { bsTooltip },
    name: 'GraphCuration',
    components: { BasicHeader, CurationTabs, D3CurationGraph },
    data() {
        return {
            dataset: 'merge',
            configTableName: null,
            configLoading: false,
            configError: null,
            /** Next recommendation_id cursor from graph curation offset API. */
            currentOffset: null,
            recordCountInput: 10,
            offsetLoading: false,
            offsetError: null,
            listLoading: false,
            listError: null,
            listPartial: false,
            recommendations: [],
            selectedRowIndex: null,
            selectedRow: null,
            detailLoading: false,
            detailError: null,
            /** Bumped on every new detail request/reset; stale async responses check this before committing. */
            detailRequestSeq: 0,
            detailGraph: null,
            /** Clone of `detailGraph` for filtering / preview (stable reference). */
            detailGraphMerged: null,
            detailCacheHit: false,
            detailCache: Object.create(null),
            /** Merge: 'both' | 'c1' | 'c2' (visual only; graph always shows both hubs). Split: 'c1'. */
            detailFocus: 'both',
            mergePreviewEnabled: false,
            /* Remembered per browser: the hint teaches the canvas once, then gets out of the way. */
            hintDismissed: readHintDismissed(),
            /** `{ ids, token }` handed to the graph to ring where the last merge landed. */
            mergeFlash: null,
            /** Which datasets have a recommendation table (config API; a 404 means none). */
            datasetAvailability: { merge: true, split: true },
            /** Field help, worded as in the user guide (Graph Curation, step 3). */
            tips: {
                dataset: 'Toggle between viewing recommendations to combine similar clusters (Merge) versus separating a single cluster into sub-clusters (Split).',
                load: 'Fetches the top algorithm-generated suggestions, ordered from highest to lowest confidence score.',
                records: 'Records specify how many suggestions to fetch. Note that if n recommendations exist, only n recommendations will be shown even if n+1 is asked for. The algorithm doesn’t generate new recommendations based on demand.',
                order: 'Recommendations are ordered from best to least confident. Saving moves past the lowest-ranked one you decided — any above it you left undecided are skipped and won\u2019t be shown again.',
                rows: 'Rows Loaded shows how many are currently displayed in the table. Note that if n recommendations exist, only n recommendations will be shown even if n+1 is asked for. The algorithm doesn’t generate new recommendations based on demand.',
            },
            /** Per-hub cap for flat comma-separated entity lists (neighbor-group strings ignore this). */
            entityGraphCap: CURATION_GRAPH_FLAT_ENTITY_CAP,
            /** Applied to graph filter (debounced from typing). */
            curationGraphIdFilter: '',
            /** Bound to the ID filter input; updates `curationGraphIdFilter` after a short delay. */
            curationGraphIdFilterInput: '',
            decisions: Object.create(null),
            recommendationSelection: Object.create(null),
            submitting: false,
            graphNodeColors: {
                cluster: '#6f42c1',
                entity: '#0d6efd',
                entity_bundle: '#6610f2',
            },
            graphNodeLabels: {
                cluster: 'Cluster',
                entity: 'Entity',
                entity_group: 'Type~domain',
                entity_bundle: 'Collapsed entities',
            },
            // ── Node movement tracking ─────────────────────────────────
            /** Merge mode: ordered list of moves for the log panel */
            mergeNodeMoves: [],
            /** Split mode: new cluster definitions */
            splitNewClusters: [],
            /** Split mode: ordered list of moves for the log panel */
            splitNodeMoves: [],
            /** UUID used for split cluster name hashing within this recommendation session */
            splitSessionUUID: null,
            /** entity_group node currently opened for ID pre-selection before drag */
            moveSelectionNode: null,
            /** Map of entityId → boolean (checked) for the pending selection */
            moveSelectionIds: {},
        }
    },
    computed: {
        cid() {
            return this.$store.state.currentClient?.cid
        },
        canLoadList() {
            if (!this.cid) return false
            if (!this.configTableName) return false
            if (this.currentOffset == null) return false
            const n = Number(this.recordCountInput)
            return Number.isFinite(n) && n >= 1
        },
        endOffset() {
            const start = Math.max(1, Number(this.currentOffset))
            if (!Number.isFinite(start)) return null
            const n = Math.max(1, Math.floor(Number(this.recordCountInput)) || 10)
            return start + n - 1
        },
        loadRangeLabel() {
            if (this.currentOffset == null || this.offsetError) return null
            const start = Math.max(1, Number(this.currentOffset))
            if (!Number.isFinite(start)) return null
            const n = Number(this.recordCountInput)
            if (!Number.isFinite(n) || n < 1) return String(start)
            const end = start + Math.floor(n) - 1
            return `${start} – ${end}`
        },
        /** Stable cache key for detail rows (recommendation-scoped). */
        activeDetailCacheKey() {
            const rid = this.activeDetailRecommendationId
            return rid != null ? this.cacheKeyForRecommendation(rid) : null
        },
        activeDetailRecommendationId() {
            if (!this.selectedRow) return null
            const r = this.selectedRow.recommendation_id
            if (r == null || String(r).trim() === '') return null
            return String(r).trim()
        },
        mergeAnchorHighlightsC1() {
            if (this.dataset !== 'merge') return false
            return this.detailFocus === 'both' || this.detailFocus === 'c1'
        },
        mergeAnchorHighlightsC2() {
            if (this.dataset !== 'merge') return false
            return this.detailFocus === 'both' || this.detailFocus === 'c2'
        },
        /** Merge graph scoped by Cluster 1 / 2 / Both (preview graph ignores focus). */
        curationGraphAfterFocus() {
            return this.applyDetailFocusToMergeGraph(this.curationGraphUnfiltered)
        },
        /** Raw graph before ID filter: live detail or merged preview (merge only). */
        curationGraphUnfiltered() {
            if (this.dataset === 'merge' && this.mergePreviewEnabled && this.activeDetailCacheKey) {
                const entry = this.detailCache[this.activeDetailCacheKey]
                if (entry && entry.rows && entry.rows.length) {
                    const effCap = this.effectiveFlatCapForRows(entry.rows)
                    const g = this.buildMergePreviewGraph(entry.rows, effCap)
                    if (g && g.nodes && g.nodes.length) return g
                }
            }
            return this.detailGraphMerged
        },
        detailGraphFilteredForDisplay() {
            const base = this.curationGraphAfterFocus
            if (!base || !base.nodes || !base.edges) return base
            const q = String(this.curationGraphIdFilter || '')
                .trim()
                .toLowerCase()
            if (!q) return base
            const nodeMatches = (n) => {
                if (n.type === 'entity') return String(n.key || '').toLowerCase().includes(q)
                if (n.type === 'entity_group') {
                    if (String(n.neighborType || n.key || '').toLowerCase().includes(q)) return true
                    const ids = n.entityIds && n.entityIds.length ? n.entityIds : []
                    if (ids.some((id) => String(id).toLowerCase().includes(q))) return true
                    const subs = n.entitySubgroups || []
                    for (let i = 0; i < subs.length; i++) {
                        const row = subs[i].entityIds || []
                        if (row.some((id) => String(id).toLowerCase().includes(q))) return true
                    }
                    return false
                }
                if (n.type === 'entity_bundle') {
                    const prev = n.bundleHiddenPreview || []
                    const all = n.bundleHiddenAllIds || []
                    if (prev.some((id) => String(id).toLowerCase().includes(q))) return true
                    if (all.some((id) => String(id).toLowerCase().includes(q))) return true
                    return String(n.key || '').toLowerCase().includes(q)
                }
                return false
            }
            const keep = new Set()
            for (let i = 0; i < base.nodes.length; i++) {
                const n = base.nodes[i]
                if (n.type === 'cluster') keep.add(n.id)
                else if (nodeMatches(n)) keep.add(n.id)
            }
            const nodes = base.nodes.filter((n) => keep.has(n.id))
            const edges = base.edges.filter((e) => keep.has(e.source) && keep.has(e.target))
            return { nodes, edges }
        },
        decisionCount() {
            return Object.keys(this.decisions).length
        },
        decisionsProgressLabel() {
            const n = this.recommendations.length
            if (!n) return '—'
            return `${this.decisionCount} / ${n}`
        },
        curationUnavailable() {
            return !this.datasetAvailability.merge && !this.datasetAvailability.split
        },
        /**
         * Index of the lowest-ranked row with a decision, or -1. Rows are ordered best first and
         * the offset is a cursor that only moves forward, so a save moves it past this row.
         */
        lastDecidedIndex() {
            const rows = this.recommendations
            for (let i = rows.length - 1; i >= 0; i--) {
                const d = this.decisions[this.rowKey(rows[i], i)]
                if (d === 'accept' || d === 'reject') return i
            }
            return -1
        },
        /** Undecided rows above the last decided one — the cursor moves past them on save. */
        skippedRecommendationCount() {
            const last = this.lastDecidedIndex
            let n = 0
            for (let i = 0; i < last; i++) {
                const d = this.decisions[this.rowKey(this.recommendations[i], i)]
                if (d !== 'accept' && d !== 'reject') n++
            }
            return n
        },
        /** Any decision is enough: saving no longer waits for every loaded row. */
        canSaveDecisions() {
            return this.lastDecidedIndex >= 0
        },
        saveDecisionsTitle() {
            const base = 'Commit your changes to the graph pipeline. Updates will reflect in the graph after the next scheduled run.'
            if (!this.canSaveDecisions) return `${base} Accept (\u2713) or reject (\u2717) at least one recommendation to enable it.`
            const skipped = this.skippedRecommendationCount
            return skipped
                ? `${base} Saves ${this.decisionCount} decision${this.decisionCount === 1 ? '' : 's'}; ${skipped} undecided recommendation${skipped === 1 ? '' : 's'} above your last decision will be skipped.`
                : base
        },
        selectionCount() {
            return Object.keys(this.recommendationSelection).length
        },
        recommendationsTable() {
            return this.configTableName
        },
        graphEntityStep() {
            return CURATION_GRAPH_ENTITY_STEP
        },
        graphEntityCapMax() {
            return CURATION_GRAPH_ENTITY_CAP_MAX
        },
        /** Detail rows (recommendation columns) cached for the selected recommendation. */
        activeDetailRows() {
            const key = this.activeDetailCacheKey
            const entry = key ? this.detailCache[key] : null
            return entry && entry.rows ? entry.rows : []
        },
        /** Merge only: `connecting_cluster_edges` records, merged across detail rows. */
        connectingClusterEdgeRecords() {
            if (this.dataset !== 'merge') return []
            const out = []
            for (const row of this.activeDetailRows) {
                out.push(...this.parseConnectingClusterEdgeRecords(row.connecting_cluster_edges))
            }
            return out
        },
        /**
         * Distinct cross-cluster edges named by the payload; both directions count once.
         *
         * Keyed by `type~domain|id`, not the bare id, because that is how the graph resolves a
         * node: one entity id can sit in two domain groups (`…_z___aqid_z_` and `…_z___aqid__p_z_`
         * both hold 6a56194b…), which is two nodes and therefore two lines. Counting bare ids here
         * would report fewer edges than the graph draws.
         */
        clusterEdgeCount() {
            const seen = new Set()
            for (const rec of this.connectingClusterEdgeRecords) {
                const a = `${rec.pairLabel}|${rec.entityId}`
                for (const grp of rec.targets) {
                    for (const id of grp.ids) {
                        const b = `${grp.pairLabel}|${id}`
                        seen.add(a < b ? `${a}::${b}` : `${b}::${a}`)
                    }
                }
            }
            return seen.size
        },
        clusterEdgeHelpText() {
            return (
                'Links between an ID in Cluster 1 and an ID in Cluster 2 in this recommendation. ' +
                'Click a group on the graph to see its connections.'
            )
        },
        /**
         * Canvas height in px, grown from the node count. A fixed viewport-derived height turns
         * any sizeable graph into an unreadable huddle; past ~24 nodes the card is allowed to
         * exceed the viewport and the page scrolls to it.
         */
        graphAreaMinHeight() {
            const n = this.detailGraph && this.detailGraph.nodes ? this.detailGraph.nodes.length : 0
            /* Grows sooner and further than a plain viewport fraction: splitting a type~domain group
               by its connecting edges raises the node count for exactly the graphs needing most room. */
            const extra = Math.max(0, n - 16) * 20
            return Math.round(Math.min(2200, 620 + extra))
        },
        /** Recommendation score for the selected row, or null when the column is null/absent. */
        recommendationScore() {
            for (const row of this.activeDetailRows) {
                const n = Number(row.score)
                if (row.score != null && row.score !== '' && Number.isFinite(n)) return n
            }
            return null
        },
        /** Largest flat (non–neighbor-group) entity list in cached detail rows for the active cluster. */
        maxFlatEntityCountInCachedDetail() {
            const key = this.activeDetailCacheKey
            if (!key) return 0
            const entry = this.detailCache[key]
            if (!entry || !entry.rows || !entry.rows.length) return 0
            return this.maxFlatEntityCountForRows(entry.rows)
        },
        canLoadMoreOntoGraph() {
            if (!this.detailGraph || this.mergePreviewEnabled) return false
            const maxR = this.maxFlatEntityCountInCachedDetail
            if (maxR <= CURATION_GRAPH_FLAT_ENTITY_CAP) return false
            return this.entityGraphCap < Math.min(maxR, CURATION_GRAPH_ENTITY_CAP_MAX)
        },
        /** True when any node has been manually moved or a new split cluster exists */
        hasNodeMoves() {
            return this.mergeNodeMoves.length > 0 || this.splitNewClusters.length > 0
        },
        totalMovesCount() {
            // mergeNodeMoves already has one entry per expanded entity ID
            // splitNodeMoves similarly expanded
            return this.mergeNodeMoves.length + this.splitNodeMoves.length
        },
        selectedMoveIdCount() {
            return Object.values(this.moveSelectionIds).filter(Boolean).length
        },
        totalMoveIds() {
            return Object.keys(this.moveSelectionIds).length
        },
        sortedMoveSelectionEntries() {
            // Selected IDs always float to the top for visual clarity
            return Object.entries(this.moveSelectionIds)
                .sort(([, a], [, b]) => (b ? 1 : 0) - (a ? 1 : 0))
        },

        /**
         * Groups splitNodeMoves by their toClusterId so the template can render
         * per-cluster removable chips without nested method calls in v-for.
         */
        splitNodeMovesByCluster() {
            const result = {}
            for (const mv of this.splitNodeMoves) {
                if (!result[mv.toClusterId]) result[mv.toClusterId] = []
                result[mv.toClusterId].push(mv)
            }
            return result
        },

        /** Cluster hubs of the loaded merge graph, by clusterIndex. */
        mergeHubNodes() {
            const graph = this.detailGraph
            if (this.dataset !== 'merge' || !graph || !graph.nodes) return { target: null, source: null }
            return {
                target: graph.nodes.find((n) => n.type === 'cluster' && Number(n.clusterIndex) === 1) || null,
                source: graph.nodes.find((n) => n.type === 'cluster' && Number(n.clusterIndex) === 2) || null,
            }
        },
        /** client_user can browse recommendations; every higher role can record and save decisions. */
        canCurate() {
            return this.$store.getters.userRoles.userRoleRank < 4
        },
        /** Whether node-move drag is active (has loaded graph, not in merge-preview, role may curate) */
        nodeMoveEnabled() {
            return this.canCurate && !!this.detailGraph && !this.mergePreviewEnabled
        },
        /**
         * nodeId → Set of entity ids already staged to move, so a fanned-out sub-node can render as
         * merged without the renderer knowing anything about the move log.
         */
        mergedEntityIdsByNode() {
            const out = {}
            for (const m of this.mergeNodeMoves) {
                if (m.entityId == null) continue
                if (!out[m.nodeId]) out[m.nodeId] = []
                out[m.nodeId].push(String(m.entityId))
            }
            return out
        },
        /**
         * Which side the bulk merge takes ids from.
         *
         * Cluster 1 is the merge target, so by default it takes them from cluster 2 — that is what
         * "merge into cluster 1" means. The fallback matters though: a connected id is by
         * definition eligible for either cluster, and on a recommendation whose connected ids all
         * sit in cluster 1 there is nothing on the c2 side to move. The old connecting-entity
         * button had no fallback and silently vanished on those rows. Here the direction reverses
         * instead, and the button says so.
         *
         * @returns {'c2'|'c1'|null} the side moved FROM, or null when nothing is connected.
         */
        bulkMergeDirection() {
            if (this.dataset !== 'merge' || !this.nodeMoveEnabled) return null
            if (this._linkedGroupsOnSide('c2').length) return 'c2'
            if (this._linkedGroupsOnSide('c1').length) return 'c1'
            return null
        },
        /**
         * Every connection group the bulk merge would stage, as the same specs the per-group merge
         * takes — so one code path stages both, and the moves table and submit payload see exactly
         * the records they already see.
         */
        bulkMergeGroups() {
            const side = this.bulkMergeDirection
            return side ? this._linkedGroupsOnSide(side) : []
        },
        bulkMergeIdCount() {
            return this.bulkMergeGroups.reduce((sum, g) => sum + g.entityIds.length, 0)
        },
        /** True once every id the bulk merge covers is staged — the button becomes the undo. */
        bulkMergeStaged() {
            const groups = this.bulkMergeGroups
            if (!groups.length) return false
            return groups.every(
                (g) => this.connectionGroupMergedIds(g.nodeId, g.entityIds).length >= g.entityIds.length
            )
        },
        bulkMergeTitle() {
            const side = this.bulkMergeDirection
            if (!side) return ''
            const from = side === 'c2' ? 2 : 1
            const to = side === 'c2' ? 1 : 2
            if (this.bulkMergeStaged) {
                return `Put all ${this.bulkMergeIdCount} back in cluster ${from}`
            }
            return `Move every id that has a cluster edge — ${this.bulkMergeIdCount} across ` +
                `${this.bulkMergeGroups.length} connection ` +
                `${this.bulkMergeGroups.length === 1 ? 'group' : 'groups'} — from cluster ${from} ` +
                `into cluster ${to}. Ids with no cluster edge are left alone; merge those by hand.`
        },

        /**
         * Final graph passed to D3CurationGraph.
         * Builds the visual split/merge state directly from move tracking arrays:
         *
         *  · entity_group PARTIAL move  → original node shrinks (remaining IDs only)
         *                                 + new node at target cluster (moved IDs only)
         *  · entity_group FULL move     → original node re-routed to target cluster
         *  · entity / entity_bundle     → whole node re-routed to target cluster
         */
        enhancedDisplayGraph() {
            let graph = this.detailGraphFilteredForDisplay
            if (!graph || !graph.nodes || !graph.edges) return graph

            // Inject new split cluster hub nodes
            if (this.dataset === 'split' && this.splitNewClusters.length) {
                const existing = new Set(graph.nodes.map(n => n.id))
                const extras = []
                for (const sc of this.splitNewClusters) {
                    if (!existing.has(sc.id)) {
                        extras.push({
                            id: sc.id, type: 'cluster', key: sc.clusterId,
                            clusterFullId: sc.clusterId, clusterIndex: sc.index,
                            hubConnectionCount: 0, totalEntityIds: 0, isSplitNew: true,
                        })
                    }
                }
                if (extras.length) graph = { nodes: [...graph.nodes, ...extras], edges: [...graph.edges] }
            }

            // Build move map from tracking arrays (mergeNodeMoves or splitNodeMoves)
            const allMoves = this.dataset === 'merge' ? this.mergeNodeMoves : this.splitNodeMoves
            if (!allMoves.length) return this.withRecomputedHubCounts(graph)

            // { nodeId: { toClusterId, movedEntityIds: Set|null } }
            const moveMap = {}
            for (const mv of allMoves) {
                if (!moveMap[mv.nodeId]) {
                    moveMap[mv.nodeId] = { toClusterId: mv.toClusterId, movedEntityIds: null }
                }
                if (mv.nodeType === 'entity_group' && mv.entityId) {
                    if (!moveMap[mv.nodeId].movedEntityIds) moveMap[mv.nodeId].movedEntityIds = new Set()
                    moveMap[mv.nodeId].movedEntityIds.add(mv.entityId)
                }
            }

            const nodeMap = new Map(graph.nodes.map(n => [n.id, n]))

            // Find the edge key (src→tgt string) for a node's cluster-membership edge.
            // 'connects' annotation edges also touch a hub, so they are skipped here.
            const findClusterEdgeKey = (nodeId) => {
                for (const e of graph.edges) {
                    if (!isMembershipLinkKind(e.linkKind)) continue
                    if (e.source === nodeId && nodeMap.get(e.target)?.type === 'cluster') return `${e.source}→${e.target}`
                    if (e.target === nodeId && nodeMap.get(e.source)?.type === 'cluster') return `${e.source}→${e.target}`
                }
                return null
            }

            /* Where the moved ids land, by type~domain.
 
               Without this a group moved into a cluster that already has that type~domain arrived
               as a second node beside it — the same `ck~aqid` twice in cluster 1, which is what a
               curator reads as the node being duplicated. Merging them is what the move means:
               these are the same type and the same domain, so after the merge they are one group
               holding the union of the ids.
 
               entity_group only. A flat `entity` node is one id and a bundle is an opaque set, so
               neither has a key to merge on. */
            const foldTarget = new Map()      // source node id → host node id
            const arrivalsByHost = new Map()  // host node id → Set of arriving ids
            for (const n of graph.nodes) {
                const move = moveMap[n.id]
                if (!move || n.type !== 'entity_group' || !n.neighborType) continue
                const toCluster = nodeMap.get(move.toClusterId)
                if (!toCluster) continue
                const wantKind = `c${Number(toCluster.clusterIndex) || 2}`
                const host = graph.nodes.find(
                    (h) => h.id !== n.id &&
                        h.type === 'entity_group' &&
                        h.neighborType === n.neighborType &&
                        h.nodeLinkKind === wantKind &&
                        /* A host on its way out of that cluster is no host at all. */
                        !moveMap[h.id]
                )
                if (!host) continue
                const originalIds = this._getEntityGroupIds(n)
                const arriving = move.movedEntityIds
                    ? originalIds.filter((id) => move.movedEntityIds.has(id))
                    : originalIds
                if (!arriving.length) continue
                foldTarget.set(n.id, host.id)
                if (!arrivalsByHost.has(host.id)) arrivalsByHost.set(host.id, new Set())
                arriving.forEach((id) => arrivalsByHost.get(host.id).add(id))
            }

            const outNodes = []
            const extraEdges = []
            const removeEdgeKeys = new Set()

            for (const n of graph.nodes) {
                /* Received ids from a group that merged into this one. */
                const arrivals = arrivalsByHost.get(n.id)
                if (arrivals && arrivals.size) {
                    const own = this._getEntityGroupIds(n)
                    const merged = [...own, ...[...arrivals].filter((id) => !own.includes(id))]
                    outNodes.push({
                        ...n,
                        entityIds: merged,
                        entitySubgroups: [{ neighborDomain: '', entityIds: merged }],
                        idCountInNode: merged.length,
                        receivedEntityIds: [...arrivals],
                        ...this._clusterEdgeSubset(n, own),
                    })
                    continue
                }
                const move = moveMap[n.id]
                if (!move) { outNodes.push(n); continue }

                const toCluster = nodeMap.get(move.toClusterId)
                if (!toCluster) { outNodes.push(n); continue }

                const idx = Number(toCluster.clusterIndex) || 2
                const newLinkKind = idx === 1 ? 'c1' : `c${idx}`

                if (n.type === 'entity_group' && move.movedEntityIds && move.movedEntityIds.size > 0) {
                    // ── PARTIAL entity_group move ──────────────────────────────────
                    const originalIds = this._getEntityGroupIds(n)
                    const movedIds   = originalIds.filter(id =>  move.movedEntityIds.has(id))
                    const remainIds  = originalIds.filter(id => !move.movedEntityIds.has(id))

                    if (remainIds.length > 0) {
                        // Keep original node with REMAINING IDs (edge to original cluster unchanged)
                        outNodes.push({
                            ...n,
                            entityIds: remainIds,
                            entitySubgroups: [{ neighborDomain: '', entityIds: remainIds }],
                            idCountInNode: remainIds.length,
                            ...this._clusterEdgeSubset(n, remainIds),
                            /* What left and where it went, so the opened group can keep each moved
                               id in place as a ghost pointing at its new node. */
                            movedOut: {
                                ids: movedIds,
                                originalIds,
                                details: n.clusterEdgeDetails || [],
                                targetId: foldTarget.get(n.id) || `${n.id}|mv`,
                                toLabel: `Cluster ${idx}`,
                            },
                        })
                    } else {
                        // All IDs moved → remove original node + its cluster edge
                        removeEdgeKeys.add(findClusterEdgeKey(n.id))
                    }

                    /* Folded into an existing group of the same type~domain — the host carries
                       these ids now, so there is no separate node and no second edge. */
                    if (movedIds.length > 0 && !foldTarget.has(n.id)) {
                        // Add a NEW node at TARGET cluster carrying the moved IDs
                        const movedId = `${n.id}|mv`
                        outNodes.push({
                            ...n,
                            id: movedId,
                            entityIds: movedIds,
                            entitySubgroups: [{ neighborDomain: '', entityIds: movedIds }],
                            idCountInNode: movedIds.length,
                            nodeLinkKind: newLinkKind,
                            isMoved: true,
                            ...this._clusterEdgeSubset(n, movedIds),
                        })
                        extraEdges.push({ source: move.toClusterId, target: movedId, linkKind: newLinkKind })
                    }
                } else if (foldTarget.has(n.id)) {
                    /* Every id went into an existing group of the same type~domain, so the node
                       itself is gone rather than re-parented. */
                    removeEdgeKeys.add(findClusterEdgeKey(n.id))
                } else {
                    // ── WHOLE-NODE move (entity, entity_bundle, or full entity_group) ──
                    removeEdgeKeys.add(findClusterEdgeKey(n.id))
                    outNodes.push({ ...n, nodeLinkKind: newLinkKind })
                    extraEdges.push({ source: move.toClusterId, target: n.id, linkKind: newLinkKind })
                }
            }

            // Rebuild edges: drop old cluster→node edges for moved nodes
            const activeIds = new Set(outNodes.map(n => n.id))
            const movedNodeIds = new Set(Object.keys(moveMap))
            const keptEdges = graph.edges.filter(e => {
                const key = `${e.source}→${e.target}`
                if (removeEdgeKeys.has(key)) return false
                /* A moved node's 'connects' annotation described where it sat before the move,
                   so drop it rather than leave a stale line into its new hub. */
                if (!isMembershipLinkKind(e.linkKind) && (movedNodeIds.has(e.source) || movedNodeIds.has(e.target))) {
                    return false
                }
                return activeIds.has(e.source) && activeIds.has(e.target)
            })

            return this.withRecomputedHubCounts(
                this._withResolvedClusterEdges({ nodes: outNodes, edges: [...keptEdges, ...extraEdges] })
            )
        },
    },
    watch: {
        cid: {
            immediate: true,
            async handler() {
                this.resetCurationScope()
                if (!this.cid) return
                const cid = this.cid
                const avail = await getCurationAvailability(this.$store, cid)
                if (cid !== this.cid) return
                this.datasetAvailability = avail
                if (!avail.merge && !avail.split) return
                /* Open on a dataset that exists. */
                if (!avail[this.dataset]) {
                    this.dataset = avail.merge ? 'merge' : 'split'
                    /* resetCurationScope ran under the old dataset; its focus belongs to that one. */
                    this.detailFocus = this.dataset === 'merge' ? 'both' : 'c1'
                }
                await Promise.all([this.refreshOffset(), this.fetchCurationConfig()])
            },
        },
        recommendations: {
            handler() {
                this.$nextTick(() => this.syncSelectAllCheckbox())
            },
        },
        recommendationSelection: {
            deep: true,
            handler() {
                this.$nextTick(() => this.syncSelectAllCheckbox())
            },
        },
        curationGraphIdFilterInput: {
            handler() {
                this.scheduleCurationIdFilterApply()
            },
        },
    },
    beforeUnmount() {
        this._dismissMergeToast()
        if (this._curationIdFilterTimer) {
            clearTimeout(this._curationIdFilterTimer)
            this._curationIdFilterTimer = null
        }
    },
    methods: {
        /**
         * Recompute each hub's `hubConnectionCount` and `totalEntityIds` from the edges of the graph
         * being displayed.
         *
         * buildGraphFromDetailRows bakes these in for the graph as loaded, but a drag moves a node
         * between clusters by rewriting membership edges — so the hub's "N nodes" label, its radius
         * and its id total all went stale the moment anything moved. Counting here, on the way to
         * the renderer, keeps them true for whatever is actually on screen (moves, cluster focus and
         * the ID filter alike). Membership edges only: a connecting or cluster edge means the node
         * links to that cluster, not that it belongs to it.
         *
         * Returns fresh node objects — `graph` may be the shared `detailGraphMerged` instance.
         */
        withRecomputedHubCounts(graph) {
            if (!graph || !graph.nodes || !graph.edges) return graph
            const nodeById = new Map(graph.nodes.map((n) => [n.id, n]))
            const degree = new Map()
            const totals = new Map()
            for (const e of graph.edges) {
                if (!isMembershipLinkKind(e.linkKind)) continue
                const src = nodeById.get(e.source)
                const tgt = nodeById.get(e.target)
                if (!src || !tgt) continue
                degree.set(e.source, (degree.get(e.source) || 0) + 1)
                degree.set(e.target, (degree.get(e.target) || 0) + 1)
                if (src.type === 'cluster') totals.set(src.id, (totals.get(src.id) || 0) + (tgt.idCountInNode || 0))
                if (tgt.type === 'cluster') totals.set(tgt.id, (totals.get(tgt.id) || 0) + (src.idCountInNode || 0))
            }
            let changed = false
            const nodes = graph.nodes.map((n) => {
                if (n.type !== 'cluster') return n
                const count = degree.get(n.id) || 0
                const total = totals.get(n.id) || 0
                if (n.hubConnectionCount === count && n.totalEntityIds === total) return n
                changed = true
                return { ...n, hubConnectionCount: count, totalEntityIds: total }
            })
            return changed ? { ...graph, nodes } : graph
        },
        scheduleCurationIdFilterApply() {
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this._curationIdFilterTimer = setTimeout(() => {
                this._curationIdFilterTimer = null
                this.curationGraphIdFilter = String(this.curationGraphIdFilterInput || '').trim()
            }, 400)
        },
        flushCurationIdFilter() {
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this.curationGraphIdFilter = String(this.curationGraphIdFilterInput || '').trim()
        },
        rowKey(row, idx) {
            if (row.recommendation_id != null && row.recommendation_id !== '') {
                return `rec:${row.recommendation_id}`
            }
            if (this.dataset === 'split') {
                const cid = row.cluster_id ?? row.cluster_id1
                return `split:${cid}::${idx}`
            }
            return `${row.cluster_id1}::${row.cluster_id2}::${idx}`
        },
        async onDatasetChange() {
            this.resetCurationScope()
            if (this.cid) {
                await Promise.all([this.refreshOffset(), this.fetchCurationConfig()])
            }
        },
        setDataset(value) {
            if (this.dataset === value) return
            this.dataset = value
            this.onDatasetChange()
        },
        resetListState() {
            this.detailRequestSeq += 1
            this.recommendations = []
            this.listError = null
            this.listPartial = false
            this.selectedRow = null
            this.selectedRowIndex = null
            this.detailGraph = null
            this.detailGraphMerged = null
            this.detailError = null
            this.detailLoading = false
            this.detailCacheHit = false
            this.detailFocus = this.dataset === 'merge' ? 'both' : 'c1'
            this.detailCache = Object.create(null)
            this.entityGraphCap = CURATION_GRAPH_FLAT_ENTITY_CAP
            this.curationGraphIdFilter = ''
            this.curationGraphIdFilterInput = ''
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this.mergePreviewEnabled = false
            this.decisions = Object.create(null)
            this.recommendationSelection = Object.create(null)
            this.resetNodeMoveState()
        },
        resetCurationScope() {
            this.resetListState()
            this.currentOffset = null
            this.recordCountInput = 10
            this.offsetError = null
            this.offsetLoading = false
            this.configTableName = null
            this.configError = null
            this.configLoading = false
        },
        cacheKeyForRecommendation(recommendationId) {
            return `${this.dataset}::rec::${String(recommendationId)}`
        },
        sqlRecommendationWhereEquals(value) {
            const s = String(value).trim()
            if (!s) return 'recommendation_id IS NULL'
            if (/^-?\d+(\.\d+)?$/.test(s) && Number.isFinite(Number(s))) {
                return `recommendation_id = ${Number(s)}`
            }
            return `recommendation_id = ${this.sqlLiteral(s)}`
        },
        decisionClass(row, idx, action) {
            const key = this.rowKey(row, idx)
            const d = this.decisions[key]
            if (d === action) {
                return action === 'accept' ? 'btn-success' : 'btn-danger'
            }
            return action === 'accept' ? 'btn-outline-success' : 'btn-outline-danger'
        },
        setDecision(row, idx, action) {
            if (!this.canCurate) return
            const key = this.rowKey(row, idx)
            const next = { ...this.decisions }
            if (next[key] === action) delete next[key]
            else next[key] = action
            this.decisions = next
        },
        isRowBulkChecked(row, idx) {
            return !!this.recommendationSelection[this.rowKey(row, idx)]
        },
        setRowBulkChecked(row, idx, checked) {
            const k = this.rowKey(row, idx)
            const next = { ...this.recommendationSelection }
            if (checked) next[k] = true
            else delete next[k]
            this.recommendationSelection = next
        },
        clearRecommendationSelection() {
            this.recommendationSelection = Object.create(null)
            this.$nextTick(() => this.syncSelectAllCheckbox())
        },
        bulkSetDecision(action) {
            if (!this.canCurate || !this.selectionCount) return
            const next = { ...this.decisions }
            this.recommendations.forEach((row, idx) => {
                const k = this.rowKey(row, idx)
                if (!this.recommendationSelection[k]) return
                next[k] = action
            })
            this.decisions = next
        },
        onSelectAllChange(e) {
            const checked = e.target.checked
            if (checked) {
                const next = { ...this.recommendationSelection }
                this.recommendations.forEach((r, i) => {
                    next[this.rowKey(r, i)] = true
                })
                this.recommendationSelection = next
            } else {
                this.recommendationSelection = Object.create(null)
            }
            this.$nextTick(() => this.syncSelectAllCheckbox())
        },
        syncSelectAllCheckbox() {
            const el = this.$refs.selectAllCheckbox
            if (!el) return
            const n = this.recommendations.length
            const c = this.selectionCount
            if (!n) {
                el.checked = false
                el.indeterminate = false
                return
            }
            if (c === 0) {
                el.checked = false
                el.indeterminate = false
            } else if (c === n) {
                el.checked = true
                el.indeterminate = false
            } else {
                el.checked = false
                el.indeterminate = true
            }
        },
        async refreshOffset() {
            if (!this.cid) return
            this.offsetLoading = true
            this.offsetError = null
            try {
                const base = this.$store.state.graphServiceUrl
                const res = await axios.get(
                    `${base}/v1/cids/${encodeURIComponent(this.cid)}/curation/offset`,
                    {
                        params: { dataset: this.dataset },
                        headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                    }
                )
                const cur = res?.data?.current_offset
                if (cur == null || !Number.isFinite(Number(cur))) {
                    this.offsetError = 'No recommendations found.'
                    this.currentOffset = null
                    return
                }
                this.currentOffset = Number(cur)
                const rc = Number(this.recordCountInput)
                if (!Number.isFinite(rc) || rc < 1) {
                    this.recordCountInput = 10
                }
            } catch (e) {
                this.currentOffset = null
                if (e.response?.status === 404) {
                    this.offsetError = 'No recommendations found.'
                } else {
                    this.offsetError =
                        e.response?.data?.message ||
                        e.response?.data?.error ||
                        e.message ||
                        'No recommendations found.'
                }
            } finally {
                this.offsetLoading = false
            }
        },
        async fetchCurationConfig() {
            if (!this.cid) return
            this.configLoading = true
            this.configError = null
            try {
                /* The availability probe already fetched this config; reuse its table. */
                const probed = (await getCurationAvailability(this.$store, this.cid)).tables?.[this.dataset]
                if (probed) {
                    this.configTableName = probed
                    return
                }
                const base = this.$store.state.graphServiceUrl
                const res = await axios.get(
                    `${base}/v1/cids/${encodeURIComponent(this.cid)}/curation/config`,
                    {
                        params: { dataset: this.dataset },
                        headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                    }
                )
                this.configTableName = res?.data?.recommendation_table_name || null
                if (!this.configTableName) {
                    this.configError = 'No recommendations found.'
                }
            } catch (e) {
                this.configTableName = null
                if (e.response?.status === 404) {
                    this.configError = 'No recommendations found.'
                } else {
                    this.configError =
                        e.response?.data?.message ||
                        e.response?.data?.error ||
                        e.message ||
                        'No recommendations found.'
                }
            } finally {
                this.configLoading = false
            }
        },
        /**
         * PUT /v1/cids/:cid/curation/offset?dataset=… — body is parseOffsetBody JSON (e.g. { current_offset }).
         * Response: { current_offset } (db.CurationOffset).
         */
        async setCurationOffset(toOffsetValue) {
            if (!this.cid) return
            const v = Number(toOffsetValue)
            if (!Number.isFinite(v)) return
            const base = this.$store.state.graphServiceUrl
            const url = `${base}/v1/cids/${encodeURIComponent(this.cid)}/curation/offset`
            const res = await axios.put(
                url,
                { current_offset: v },
                {
                    params: { dataset: this.dataset },
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            )
            const cur = res.data?.current_offset
            this.currentOffset =
                cur != null && Number.isFinite(Number(cur)) ? Number(cur) : v
        },
        /**
         * Next cursor after this batch: one past the lowest-ranked decided row's recommendation_id.
         * The list loads `recommendation_id between start and start+count-1`, so the cursor is an
         * id, not a position — ids can have gaps, and counting rows would land back inside the
         * decided window.
         */
        computeNextCurationOffsetAfterSubmit() {
            const start = Math.max(1, Number(this.currentOffset))
            if (!Number.isFinite(start)) return null
            /* Past the lowest-ranked decided row, not past the whole batch: undecided rows below
               it come back on the next load, undecided rows above it are skipped. */
            const last = this.lastDecidedIndex
            if (last < 0) return null
            const id = Number(this.recommendations[last]?.recommendation_id)
            return Number.isFinite(id) ? Math.max(start, id + 1) : start + last + 1
        },
        /**
         * When merge + Cluster 1/2 focus, show only that hub and its entity edges (linkKind c1/c2).
         * Merge preview (single hub) ignores focus so the graph stays visible.
         */
        applyDetailFocusToMergeGraph(graph) {
            if (!graph || !graph.nodes || !graph.edges) return graph
            if (this.dataset !== 'merge' || this.mergePreviewEnabled) return graph
            const f = this.detailFocus
            if (f === 'both') return graph
            const kindKeep = f === 'c2' ? 'c2' : 'c1'
            const wantIndex = f === 'c2' ? 2 : 1

            /* Primary: find the hub node by clusterIndex (set by buildGraphFromDetailRows
               as 1 or 2). This works for both real API and mock data because it reads
               directly from the built graph instead of matching selectedRow.cluster_id*
               against graph node IDs — those IDs differ on the mock server since the
               list and detail queries return different random cluster IDs. */
            const clusterNodes = graph.nodes.filter((n) => n.type === 'cluster')
            let hubNode = clusterNodes.find((n) => Number(n.clusterIndex) === wantIndex)

            /* Fallback: derive from selectedRow (covers edge cases where clusterIndex
               is absent, e.g. older cached graph shapes). */
            if (!hubNode) {
                const row = this.selectedRow
                if (!row) return graph
                const cid = f === 'c2'
                    ? String(row.cluster_id2 ?? '').trim()
                    : String(row.cluster_id1 ?? row.cluster_id ?? '').trim()
                if (!cid) return graph
                hubNode = graph.nodes.find((n) => n.id === `cluster:${cid}`)
            }

            if (!hubNode) return graph
            const hubKeep = hubNode.id
            const keep = new Set([hubKeep])
            for (let i = 0; i < graph.edges.length; i++) {
                const e = graph.edges[i]
                if ((e.linkKind || '') !== kindKeep) continue
                if (e.source === hubKeep) keep.add(e.target)
                else if (e.target === hubKeep) keep.add(e.source)
            }
            const nodes = graph.nodes.filter((n) => keep.has(n.id))
            const ids = new Set(nodes.map((n) => n.id))
            const edgesOut = graph.edges.filter(
                (e) => ids.has(e.source) && ids.has(e.target) && (e.linkKind || '') === kindKeep
            )
            return { nodes, edges: edgesOut }
        },
        splitRowClusterId(row) {
            const v = row.cluster_id ?? row.cluster_id1
            return v != null && v !== '' ? String(v) : '—'
        },
        /** Abbreviate a cluster ID for the strip: first 8 chars … last 4 chars. */
        shortClusterId(id) {
            if (!id) return '—'
            const s = String(id)
            return s.length > 16 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s
        },
        /**
         * A Pail map keyed by `entity_type~entity_domain` — the same label the graph uses for its
         * entity_group nodes — into `{pairLabel, ids}` groups. This is the shape of the nested
         * value inside each `connecting_cluster_edges` record.
         *
         * Accepted shapes:
         *  - Athena/Presto map literal: `{ck~aqfer=[id1], em~aqfer=[id2, id3]}`
         *  - JSON / real object map:    `{"ck~aqfer": ["id1"]}`
         *  - map as key/value entries:  `[{key=ck~aqfer, value=[id1]}, …]` or the JSON
         *    `[{"key": "ck~aqfer", "value": ["id1"]}]` — how some tables serialize a map column
         *  - flat array / bracket-string / csv → a single unkeyed group
         *
         * @returns {Array<{pairLabel: string, ids: string[]}>} pairLabel is '' when unkeyed.
         */
        parsePairLabelIdMap(raw) {
            if (raw == null || raw === '') return []
            const asMap = (obj) =>
                Object.entries(obj)
                    .map(([pairLabel, ids]) => ({
                        pairLabel: String(pairLabel).trim(),
                        ids: [...new Set(this.parseEntityList(ids).map((x) => String(x).trim()).filter(Boolean))],
                    }))
                    .filter((g) => g.ids.length)

            const entries = this.parseKeyValueEntries(raw)
            if (entries) return asMap(entries)

            if (typeof raw === 'object' && !Array.isArray(raw)) return asMap(raw)

            if (typeof raw === 'string' && raw.trim().startsWith('{')) {
                const s = raw.trim()
                try {
                    const j = JSON.parse(s)
                    if (j && typeof j === 'object' && !Array.isArray(j)) return asMap(j)
                } catch (_) {
                    /* not JSON — fall through to the Presto map literal below */
                }
                /* `{key=[v1, v2], key2=[v3]}` — keys may contain '~', values may lack brackets. */
                const out = []
                const entryRe = /([^,{}=]+?)\s*=\s*(?:\[([^\]]*)\]|([^,{}]*))/g
                let m
                while ((m = entryRe.exec(s)) !== null) {
                    const pairLabel = String(m[1]).replace(/^[{\s]+/, '').trim()
                    const ids = [
                        ...new Set(
                            this.parseEntityList(m[2] != null ? m[2] : m[3])
                                .map((x) => String(x).trim())
                                .filter(Boolean)
                        ),
                    ]
                    if (pairLabel && ids.length) out.push({ pairLabel, ids })
                }
                if (out.length) return out
            }

            const flat = [...new Set(this.parseEntityList(raw).map((x) => String(x).trim()).filter(Boolean))]
            return flat.length ? [{ pairLabel: '', ids: flat }] : []
        },
        /**
         * A map serialized as a list of `{key, value}` entries, folded back into a plain
         * `{key: ids[]}` object (entries repeating a key are concatenated).
         *
         * @returns {Object<string, Array>|null} null when `raw` is not in this shape, so the
         *   caller falls through to the other map forms.
         */
        parseKeyValueEntries(raw) {
            let list = raw
            if (typeof raw === 'string') {
                const s = raw.trim()
                if (!s.startsWith('[')) return null
                try {
                    list = JSON.parse(s)
                } catch (_) {
                    /* Presto literal `[{key=ck~aqfer, value=[id1, id2]}, …]`. */
                    list = this.splitTopLevelBraceGroups(s).map((grp) => {
                        const k = grp.match(/\bkey\s*=\s*([^,{}[\]]+)/i)
                        const v = grp.match(/\bvalue\s*=\s*(?:\[([^\]]*)\]|([^,{}[\]]+))/i)
                        return k ? { key: k[1], value: v ? (v[1] != null ? v[1] : v[2]) : '' } : null
                    })
                }
            }
            if (!Array.isArray(list) || !list.length) return null
            if (!list.every((e) => e && typeof e === 'object' && !Array.isArray(e) && 'key' in e)) return null
            const out = {}
            for (const { key, value } of list) {
                const k = String(key ?? '').trim()
                if (!k) continue
                out[k] = (out[k] || []).concat(this.parseEntityList(value))
            }
            return out
        },
        /**
         * `connecting_cluster_edges` (merge recommendations) holds the graph edges that actually
         * join the two clusters — one record per entity, naming the entities in the other cluster
         * it links to. These are real edges and may cross type~domain boundaries, e.g. a
         * `node~aqfer` entity linking a `user~aqfer` one. An empty nested map means the entity is
         * in the cluster but joins nothing on the other side.
         *
         * Pail returns it as an array of structs:
         *   [{entity_id=id0, entity_type=ck, entity_domain=aqfer,
         *     connecting_cluster_edges={ck~aqfer=[id1, id3]}}, …]
         * or, from tables that serialize the map as entries:
         *   [{entity_id=id0, entity_type=ck, entity_domain=aqfer,
         *     connecting_cluster_edges=[{key=ck~aqfer, value=[id1, id3]}]}, …]
         * The nested value is an `entity_type~entity_domain` map, parsed with parsePairLabelIdMap.
         * Records whose map is empty are dropped: they carry no edge, and an id absent from this
         * column is treated identically by the graph builder.
         *
         * @returns {Array<{entityId: string, pairLabel: string, targets: Array<{pairLabel: string, ids: string[]}>}>}
         */
        parseConnectingClusterEdgeRecords(raw) {
            if (raw == null || raw === '') return []
            const out = []
            const pushRecord = (entityId, entityType, entityDomain, targetsRaw) => {
                const id = String(entityId ?? '').trim()
                if (!id) return
                const t = String(entityType ?? '').trim()
                const d = String(entityDomain ?? '').trim()
                const pairLabel = t && d ? `${t}~${d}` : t
                const targets = this.parsePairLabelIdMap(targetsRaw)
                if (!targets.length) return
                out.push({ entityId: id, pairLabel, targets })
            }
            const fromObject = (o) => {
                if (!o || typeof o !== 'object' || Array.isArray(o)) return
                const keys = Object.keys(o)
                const find = (re) => keys.find((k) => re.test(k))
                const idKey = find(/^entity_?id$/i) || find(/entity_?id/i)
                const typeKey = find(/entity_?type/i)
                const domainKey = find(/entity_?domain/i)
                const edgeKey =
                    find(/cluster_?edges/i) ||
                    keys.find((k) => k !== idKey && k !== typeKey && k !== domainKey && o[k] && typeof o[k] === 'object')
                pushRecord(
                    idKey ? o[idKey] : null,
                    typeKey ? o[typeKey] : null,
                    domainKey ? o[domainKey] : null,
                    edgeKey ? o[edgeKey] : null
                )
            }

            if (Array.isArray(raw)) {
                raw.forEach(fromObject)
                return out
            }
            if (typeof raw === 'object') {
                fromObject(raw)
                return out
            }
            const s = String(raw).trim()
            if (!s) return []
            if (s.startsWith('[') || s.startsWith('{')) {
                try {
                    const j = JSON.parse(s)
                    if (Array.isArray(j)) {
                        j.forEach(fromObject)
                        if (out.length) return out
                    } else if (j && typeof j === 'object') {
                        fromObject(j)
                        if (out.length) return out
                    }
                } catch (_) {
                    /* not JSON — fall through to the Presto struct literal below */
                }
            }
            /* Struct literal. Values never contain braces, and the nested map is the only
               brace-delimited part of a record, so a depth scan separates records cleanly.
               The map is either `{ck~aqfer=[…]}` or its entry-list form `[{key=ck~aqfer, value=[…]}]`. */
            for (const rec of this.splitTopLevelBraceGroups(s)) {
                const idM = rec.match(/entity_?id\s*=\s*([^,{}[\]]+)/i)
                if (!idM) continue
                const typeM = rec.match(/entity_?type\s*=\s*([^,{}[\]]+)/i)
                const domainM = rec.match(/entity_?domain\s*=\s*([^,{}[\]]+)/i)
                const inner = this.extractBalancedBlock(rec, /(?:connecting_)?cluster_edges\s*=\s*/i)
                if (!inner) continue
                pushRecord(idM[1], typeM && typeM[1], domainM && domainM[1], inner)
            }
            return out
        },
        /** Top-level `{...}` groups of a string, nested braces kept intact. */
        splitTopLevelBraceGroups(s) {
            const out = []
            let depth = 0
            let start = -1
            for (let i = 0; i < s.length; i++) {
                const ch = s[i]
                if (ch === '{') {
                    if (depth === 0) start = i
                    depth += 1
                } else if (ch === '}') {
                    if (depth > 0) depth -= 1
                    if (depth === 0 && start >= 0) {
                        out.push(s.slice(start, i + 1))
                        start = -1
                    }
                }
            }
            return out
        },
        /** The balanced `{...}` or `[...]` block that follows the first match of `re`, or ''. */
        extractBalancedBlock(s, re) {
            const m = s.match(re)
            if (!m) return ''
            let i = m.index + m[0].length
            while (i < s.length && /\s/.test(s[i])) i += 1
            const open = s[i]
            const close = { '{': '}', '[': ']' }[open]
            if (!close) return ''
            let depth = 0
            for (let j = i; j < s.length; j++) {
                if (s[j] === open) depth += 1
                else if (s[j] === close) {
                    depth -= 1
                    if (depth === 0) return s.slice(i, j + 1)
                }
            }
            return ''
        },
        /**
         * @param {{ skipOffsetRefresh?: boolean }} [opts] - If true, caller already refreshed offset; only run the Pail list query.
         */
        async loadRecommendations(opts = {}) {
            const skipOffsetRefresh = opts.skipOffsetRefresh === true
            if (!skipOffsetRefresh && !this.canLoadList) return
            if (
                skipOffsetRefresh &&
                (!this.cid ||
                    this.currentOffset == null ||
                    !Number.isFinite(Number(this.recordCountInput)) ||
                    Number(this.recordCountInput) < 1)
            ) {
                return
            }
            this.listLoading = true
            this.listError = null
            this.listPartial = false
            this.recommendationSelection = Object.create(null)
            this.detailError = null
            this.selectedRow = null
            this.selectedRowIndex = null
            this.detailGraph = null
            this.detailGraphMerged = null
            this.mergePreviewEnabled = false
            this.curationGraphIdFilter = ''
            this.curationGraphIdFilterInput = ''
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            if (!skipOffsetRefresh) {
                await this.refreshOffset()
            }
            if (this.currentOffset == null) {
                this.listLoading = false
                return
            }
            const start = Math.max(1, Number(this.currentOffset))
            const count = Math.max(1, Math.floor(Number(this.recordCountInput)) || 10)
            const end = start + count - 1
            const tbl = this.recommendationsTable
            const sql =
                this.dataset === 'split'
                    ? `select recommendation_id, cluster_id from ${tbl} where recommendation_id between ${start} and ${end}`
                    : [
                          'select recommendation_id, cluster_id1, cluster_id2',
                          `from ${tbl}`,
                          `where recommendation_id between ${start} and ${end}`,
                      ].join(' ')
            try {
                const raw = await this.runPailQuery(sql)
                this.listPartial = !!raw.partial_result
                const rows = this.rowsFromResult(raw)
                rows.sort((a, b) => {
                    const an = Number(a.recommendation_id)
                    const bn = Number(b.recommendation_id)
                    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn
                    return String(a.recommendation_id ?? '').localeCompare(String(b.recommendation_id ?? ''))
                })
                this.recommendations = rows
                this.decisions = Object.create(null)
            } catch (e) {
                this.recommendations = []
                this.listError = e.message || 'Failed to load recommendations.'
            } finally {
                this.listLoading = false
            }
        },
        selectRow(row, idx) {
            this.detailFocus = this.dataset === 'merge' ? 'both' : 'c1'
            this.entityGraphCap = CURATION_GRAPH_FLAT_ENTITY_CAP
            this.curationGraphIdFilter = ''
            this.curationGraphIdFilterInput = ''
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this.mergePreviewEnabled = false
            this.resetNodeMoveState()
            this.selectedRow = row
            this.selectedRowIndex = idx
            this.loadDetailForRow()
        },
        selectRowAndFocus(row, idx, focus) {
            this.detailFocus =
                this.dataset === 'split' ? 'c1' : focus === 'c2' ? 'c2' : focus === 'both' ? 'both' : 'c1'
            this.entityGraphCap = CURATION_GRAPH_FLAT_ENTITY_CAP
            this.curationGraphIdFilter = ''
            this.curationGraphIdFilterInput = ''
            if (this._curationIdFilterTimer) {
                clearTimeout(this._curationIdFilterTimer)
                this._curationIdFilterTimer = null
            }
            this.mergePreviewEnabled = false
            this.resetNodeMoveState()
            this.selectedRow = row
            this.selectedRowIndex = idx
            this.loadDetailForRow()
        },
        setDetailFocus(focus) {
            if (!this.selectedRow) return
            if (this.dataset === 'split') {
                this.detailFocus = 'c1'
                return
            }
            const next = focus === 'c2' ? 'c2' : focus === 'c1' ? 'c1' : 'both'
            if (this.detailFocus === next) return
            this.detailFocus = next
        },
        async loadDetailForRow() {
            /* Bump the request generation so a stale response from a row/dataset
               switched away from while this call was in flight is ignored below. */
            this.detailRequestSeq += 1
            const seq = this.detailRequestSeq
            const recId = this.activeDetailRecommendationId
            if (!recId) {
                this.detailGraph = null
                this.syncDetailGraphMerged()
                return
            }
            const cacheKey = this.cacheKeyForRecommendation(recId)
            this.detailError = null
            this.detailCacheHit = !!this.detailCache[cacheKey]
            const cached = this.detailCache[cacheKey]
            if (cached) {
                const rows = cached.rows
                if (rows && rows.length) {
                    const effCap = this.effectiveFlatCapForRows(rows)
                    const graph = this.buildGraphFromDetailRows(rows, effCap)
                    this.detailGraph = graph
                } else {
                    this.detailGraph = cached.graph || cached
                }
                this.syncDetailGraphMerged()
                return
            }
            this.detailLoading = true
            this.detailGraph = null
            this.detailGraphMerged = null
            try {
                const { rows, graph } = await this.fetchClusterGraph(recId)
                if (seq !== this.detailRequestSeq) return
                this.detailGraph = graph
                if (graph && rows && rows.length) {
                    this.detailCache[cacheKey] = { rows, graph }
                } else if (graph) {
                    this.detailCache[cacheKey] = { rows: rows || [], graph }
                }
                this.syncDetailGraphMerged()
            } catch (e) {
                if (seq !== this.detailRequestSeq) return
                this.detailError = e.message || 'Failed to load cluster detail.'
            } finally {
                if (seq === this.detailRequestSeq) this.detailLoading = false
            }
        },
        sqlLiteral(value) {
            return String(value).replace(/'/g, "''")
        },
        async fetchClusterGraph(recommendationId) {
            const wh = this.sqlRecommendationWhereEquals(recommendationId)
            const tbl = this.recommendationsTable
            /* `connecting_entity_ids` is deliberately not selected. It only restated the bridge as a
               flat type~domain → ids map — "these ids could sit in either cluster" — with no
               indication of which entity on the other side any of them answers to. Two overlapping
               cross-cluster encodings on one canvas read as contradictory, so the graph now speaks
               only in `connecting_cluster_edges`, which names both endpoints of every edge. */
            const mergeCols = [
                'cluster_id1',
                'cluster_id2',
                'score',
                'connecting_cluster_edges',
                'entity_list1',
                'entity_list2',
            ]
            const mergeSql = (cols) => `select ${cols.join(', ')} from ${tbl} where ${wh}`
            let raw
            if (this.dataset === 'split') {
                raw = await this.runPailQuery(`select cluster_id, entity_list, score from ${tbl} where ${wh}`)
            } else {
                try {
                    raw = await this.runPailQuery(mergeSql(mergeCols))
                } catch (e) {
                    /* `connecting_cluster_edges` is newer than some recommendation tables. When the
                       column is missing the query fails to resolve it, so retry on the legacy column
                       list instead of leaving the user with no graph at all. */
                    if (!/connecting_cluster_edges/i.test(e?.message || '')) throw e
                    raw = await this.runPailQuery(mergeSql(mergeCols.filter((c) => c !== 'connecting_cluster_edges')))
                }
            }
            const rows = this.rowsFromResult(raw)
            const effCap = this.effectiveFlatCapForRows(rows)
            const graph = this.buildGraphFromDetailRows(rows, effCap)
            return { rows, graph }
        },
        maxFlatEntityCountForRows(rows) {
            if (!rows || !rows.length) return 0
            const cols =
                this.dataset === 'split'
                    ? ['entity_list']
                    : ['entity_list1', 'entity_list2', 'entity_list']
            let m = 0
            for (const row of rows) {
                for (const col of cols) {
                    const raw = row[col]
                    if (raw == null || raw === '') continue
                    /* Neighbor-group lists are never capped per-hub, so they don't count towards
                       the flat cap. Detect them the same way the graph builder does — the schema
                       field names are entity_type/entity_domain, but Pail also returns the
                       entity_neighbor_* variant. */
                    if (this.parseNeighborEntityGroups(raw).length) continue
                    const norm = this.normalizePailEntityListField(raw)
                    const n = this.parseEntityList(norm).length
                    m = Math.max(m, n)
                }
            }
            return m
        },
        effectiveFlatCapForRows(rows) {
            const maxR = this.maxFlatEntityCountForRows(rows)
            if (maxR < 1) return this.entityGraphCap
            return Math.min(this.entityGraphCap, CURATION_GRAPH_ENTITY_CAP_MAX, maxR)
        },
        loadMoreGraphEntitiesOnGraph() {
            const key = this.activeDetailCacheKey
            if (!key) return
            const entry = this.detailCache[key]
            if (!entry || !entry.rows || !entry.rows.length) return
            const maxR = this.maxFlatEntityCountForRows(entry.rows)
            const next = Math.min(
                this.entityGraphCap + CURATION_GRAPH_ENTITY_STEP,
                CURATION_GRAPH_ENTITY_CAP_MAX,
                maxR
            )
            if (next <= this.entityGraphCap) return
            this.entityGraphCap = next
            const effCap = this.effectiveFlatCapForRows(entry.rows)
            const graph = this.buildGraphFromDetailRows(entry.rows, effCap)
            this.detailGraph = graph
            this.detailCache[key] = { ...this.detailCache[key], graph }
            this.syncDetailGraphMerged()
        },
        /** Clone of `detailGraph` for stable reference (no session composites). */
        syncDetailGraphMerged() {
            const base = this.detailGraph
            if (!base || !base.nodes || !base.edges) {
                this.detailGraphMerged = null
                return
            }
            this.detailGraphMerged = {
                nodes: base.nodes.map((n) => ({ ...n })),
                edges: base.edges.map((e) => ({ ...e })),
                clusterEdgeStats: base.clusterEdgeStats,
            }
        },
        /**
         * Merge preview (merge dataset only): cluster 2 folded into cluster 1 hub — same type~domain or same flat id
         * merges ids; keys only on cluster 2 are flagged mergePreviewNew.
         */
        buildMergePreviewGraph(rows, flatCap = CURATION_GRAPH_FLAT_ENTITY_CAP) {
            const nodes = []
            const edgeKeys = new Set()
            const edges = []
            const pushEdge = (source, target, linkKind) => {
                const ek = `${source}->${target}`
                if (edgeKeys.has(ek)) return
                edgeKeys.add(ek)
                const e = { source, target }
                if (linkKind) e.linkKind = linkKind
                edges.push(e)
            }
            const addNode = (id, type, key, extras = {}) => {
                if (!id || nodes.some((n) => n.id === id)) return
                nodes.push({
                    id,
                    type,
                    key: key || id,
                    ...extras,
                })
            }
            let mainC1 = null
            rows.forEach((row) => {
                const c1 = row.cluster_id1 || row.cluster_id
                if (c1 && !mainC1) mainC1 = c1
            })
            if (!mainC1) return null
            const hubId = `cluster:${mainC1}`
            addNode(hubId, 'cluster', mainC1, { mergePreview: true, hubConnectionCount: 0, clusterIndex: 1 })

            const capFlat = Math.max(1, Math.floor(Number(flatCap)) || CURATION_GRAPH_FLAT_ENTITY_CAP)
            const pairAccum = new Map()
            const ingestPair = (pairLabel, ids, side) => {
                let o = pairAccum.get(pairLabel)
                if (!o) {
                    o = { ids: new Set(), inC1: false, inC2: false }
                    pairAccum.set(pairLabel, o)
                }
                ids.forEach((id) => o.ids.add(id))
                if (side === 'c1') o.inC1 = true
                else o.inC2 = true
            }
            const flatC1 = new Set()
            const flatC2 = new Set()

            const ingestFlat = (rawList, side) => {
                if (rawList == null || rawList === '') return
                const groups = this.parseNeighborEntityGroups(rawList)
                if (groups.length) {
                    groups.forEach((g) => {
                        const t = String(g.neighborType || '').trim() || 'unknown'
                        const d = String(g.domain || '').trim() || 'default'
                        const pairLabel = `${t}~${d}`
                        const ids = (g.ids || []).map(String).filter(Boolean)
                        if (ids.length) ingestPair(pairLabel, ids, side)
                    })
                    return
                }
                const norm = this.normalizePailEntityListField(rawList)
                const all = this.parseEntityList(norm).map(String).filter(Boolean)
                const shown = all.slice(0, capFlat)
                const target = side === 'c1' ? flatC1 : flatC2
                shown.forEach((id) => target.add(id))
                const overflow = all.length - shown.length
                if (overflow > 0) {
                    const hidden = all.slice(capFlat)
                    const preview = hidden.slice(0, CURATION_BUNDLE_ID_PREVIEW)
                    const bid = `${hubId}|pv|bundle|${side}`
                    addNode(bid, 'entity_bundle', `+${overflow}`, {
                        entityLegendKey: 'entity_bundle',
                        bundleTotal: all.length,
                        bundleDisplayed: shown.length,
                        bundleOverflow: overflow,
                        bundleHiddenPreview: preview,
                        bundleHiddenAllIds: hidden,
                        bundleHiddenNotListed: Math.max(0, hidden.length - preview.length),
                        bundleLinkKind: side === 'c1' ? 'c1' : 'c2',
                        mergePreviewNew: side === 'c2',
                    })
                    pushEdge(hubId, bid, side === 'c1' ? 'c1' : 'c2')
                }
            }

            rows.forEach((row) => {
                const raw1 = row.entity_list ?? row.entity_list1
                const raw2 = row.entity_list2
                ingestFlat(raw1, 'c1')
                if (raw2 != null && raw2 !== '') ingestFlat(raw2, 'c2')
            })

            let pairIdx = 0
            pairAccum.forEach((o, pairLabel) => {
                const entityIds = [...o.ids]
                if (!entityIds.length) return
                const mergePreviewNew = o.inC2 && !o.inC1
                const nid = `${hubId}|pv|td|${pairIdx++}`
                addNode(nid, 'entity_group', pairLabel, {
                    neighborType: pairLabel,
                    entitySubgroups: [{ neighborDomain: '', entityIds }],
                    entityIds,
                    entityLegendKey: `entity:${pairLabel}`,
                    mergeGroupDimension: 'pair',
                    idCountInNode: entityIds.length,
                    mergePreviewNew,
                })
                pushEdge(hubId, nid, 'c1')
            })

            const flatAll = new Set([...flatC1, ...flatC2])
            let flatIdx = 0
            flatAll.forEach((eid) => {
                const in1 = flatC1.has(eid)
                const in2 = flatC2.has(eid)
                const mergePreviewNew = in2 && !in1
                const nid = `${hubId}|pv|flat|${flatIdx++}`
                addNode(nid, 'entity', eid, {
                    entityLegendKey: 'entity',
                    idCountInNode: 1,
                    mergePreviewNew,
                    inBothClusters: in1 && in2,
                })
                pushEdge(hubId, nid, 'c1')
            })

            if (nodes.length <= 1) return null
            const deg = new Map()
            for (let ei = 0; ei < edges.length; ei++) {
                const e = edges[ei]
                deg.set(e.source, (deg.get(e.source) || 0) + 1)
                deg.set(e.target, (deg.get(e.target) || 0) + 1)
            }
            for (let ni = 0; ni < nodes.length; ni++) {
                const n = nodes[ni]
                if (n.type === 'cluster') {
                    n.hubConnectionCount = deg.get(n.id) || 0
                } else if (n.type === 'entity' && n.idCountInNode == null) {
                    n.idCountInNode = 1
                } else if (n.type === 'entity_group' && n.idCountInNode == null && n.entityIds) {
                    n.idCountInNode = n.entityIds.length
                }
            }
            const nodeByIdP = new Map(nodes.map(n => [n.id, n]))
            for (let ei = 0; ei < edges.length; ei++) {
                const e = edges[ei]
                const src = nodeByIdP.get(e.source)
                const tgt = nodeByIdP.get(e.target)
                if (src && src.type === 'cluster' && tgt) {
                    src.totalEntityIds = (src.totalEntityIds || 0) + (tgt.idCountInNode || 0)
                }
                if (tgt && tgt.type === 'cluster' && src) {
                    tgt.totalEntityIds = (tgt.totalEntityIds || 0) + (src.idCountInNode || 0)
                }
            }
            return { nodes, edges }
        },
        buildGraphFromDetailRows(rows, flatCap = CURATION_GRAPH_FLAT_ENTITY_CAP) {
            const nodes = []
            const edgeKeys = new Set()
            const edges = []
            const pushEdge = (source, target, linkKind, extras) => {
                const ek = `${source}->${target}`
                if (edgeKeys.has(ek)) return
                edgeKeys.add(ek)
                const e = { source, target }
                if (linkKind) e.linkKind = linkKind
                if (extras) Object.assign(e, extras)
                edges.push(e)
            }
            const addNode = (id, type, key, extras = {}) => {
                if (!id || nodes.some((n) => n.id === id)) return
                nodes.push({
                    id,
                    type,
                    key: key || id,
                    ...extras,
                })
            }
            const hub = (cid) => `cluster:${cid}`
            const capFlat = Math.max(1, Math.floor(Number(flatCap)) || CURATION_GRAPH_FLAT_ENTITY_CAP)

            const clusterEdgeRecords = []
            rows.forEach((row) => {
                for (const rec of this.parseConnectingClusterEdgeRecords(row.connecting_cluster_edges)) {
                    clusterEdgeRecords.push(rec)
                }
            })

            /**
             * `pairLabel|id` → the set of type~domain groups on the other side that id is joined to.
             * Both directions are recorded: a record may be written from either cluster's side, so
             * without the reverse half the far node could not say what it is joined to, and the
             * edges would only be describable from one end.
             */
            const edgeSignatures = new Map()
            const noteSignature = (pairLabel, id, targetPairLabel) => {
                const k = `${String(pairLabel || '')}|${String(id)}`
                let set = edgeSignatures.get(k)
                if (!set) {
                    set = new Set()
                    edgeSignatures.set(k, set)
                }
                if (targetPairLabel != null) set.add(String(targetPairLabel))
            }
            for (const rec of clusterEdgeRecords) {
                noteSignature(rec.pairLabel, rec.entityId, null)
                for (const grp of rec.targets) {
                    noteSignature(rec.pairLabel, rec.entityId, grp.pairLabel)
                    for (const id of grp.ids) noteSignature(grp.pairLabel, id, rec.pairLabel)
                }
            }
            const signatureFor = (pairLabel, id) => {
                const set = edgeSignatures.get(`${String(pairLabel || '')}|${String(id)}`)
                if (!set || !set.size) return null
                return [...set].sort()
            }

            /**
             * How one type~domain group divides by the connecting edges its members take — the
             * sub-nodes the renderer fans out when the node is opened.
             *
             * Bucketed by the whole signature, not one bucket per target group, so every id appears
             * in exactly one sub-node. Per-target buckets would repeat an id in as many sub-nodes as
             * it has edges, and each sub-node offers a merge that moves its ids: the same id would
             * then be merged twice from two buttons that both looked correct.
             *
             * @returns {Array<{key: string, targets: string[], ids: string[]}>} widest relation
             *   first, the ids joined to nothing last. Empty when the group has no edges at all.
             */
            const buildRelationGroups = (pairLabel, entityIds) =>
                this._bucketRelationGroups(entityIds, (eid) => signatureFor(pairLabel, eid))

            const attachEntityList = (hubNodeId, rawList, linkKind) => {
                if (rawList == null || rawList === '') return
                const groups = this.parseNeighborEntityGroups(rawList)
                if (groups.length) {
                    const byPair = new Map()
                    groups.forEach((g) => {
                        const t = String(g.neighborType || '').trim() || 'unknown'
                        const d = String(g.domain || '').trim() || 'default'
                        const pairLabel = `${t}~${d}`
                        const ids = (g.ids || []).map(String).filter(Boolean)
                        if (!ids.length) return
                        const prev = byPair.get(pairLabel) || []
                        byPair.set(pairLabel, prev.concat(ids))
                    })
                    let pairIdx = 0
                    byPair.forEach((rawIds, pairLabel) => {
                        const entityIds = [...new Set(rawIds.map(String))].filter(Boolean)
                        if (!entityIds.length) return
                        const nid = `${hubNodeId}|td|${pairIdx++}`
                        addNode(nid, 'entity_group', pairLabel, {
                            neighborType: pairLabel,
                            entitySubgroups: [{ neighborDomain: '', entityIds }],
                            entityIds,
                            entityLegendKey: `entity:${pairLabel}`,
                            mergeGroupDimension: 'pair',
                            idCountInNode: entityIds.length,
                            nodeLinkKind: linkKind,
                            /* How this group WOULD divide by connecting edge. Precomputed here but
                               not drawn: the canvas stays one node per type~domain, and the
                               renderer fans these out as sub-nodes only when the node is opened. */
                            relationGroups: buildRelationGroups(pairLabel, entityIds),
                        })
                        pushEdge(hubNodeId, nid, linkKind)
                    })
                } else {
                    const norm = this.normalizePailEntityListField(rawList)
                    const all = this.parseEntityList(norm).map(String).filter(Boolean)
                    const cap = capFlat
                    const shown = all.slice(0, cap)
                    shown.forEach((eid) => {
                        const nid = `${hubNodeId}|__flat__|${eid}`
                        addNode(nid, 'entity', eid, { entityLegendKey: 'entity', idCountInNode: 1, nodeLinkKind: linkKind })
                        pushEdge(hubNodeId, nid, linkKind)
                    })
                    const overflow = all.length - shown.length
                    if (overflow > 0) {
                        const hidden = all.slice(cap)
                        const preview = hidden.slice(0, CURATION_BUNDLE_ID_PREVIEW)
                        const bid = `${hubNodeId}|__bundle__|${linkKind}`
                        addNode(bid, 'entity_bundle', `+${overflow}`, {
                            entityLegendKey: 'entity_bundle',
                            bundleTotal: all.length,
                            bundleDisplayed: shown.length,
                            bundleOverflow: overflow,
                            bundleHiddenPreview: preview,
                            /** Full list of ids not drawn as individual nodes (for download / expand-in-panel). */
                            bundleHiddenAllIds: hidden,
                            bundleHiddenNotListed: Math.max(0, hidden.length - preview.length),
                            bundleLinkKind: linkKind,
                        })
                        pushEdge(hubNodeId, bid, linkKind)
                    }
                }
            }
            rows.forEach((row) => {
                const c1 = row.cluster_id1 || row.cluster_id
                const c2 = row.cluster_id2
                if (!c1) return
                const h1 = hub(c1)
                addNode(h1, 'cluster', c1, { clusterIndex: 1, clusterFullId: c1 })
                if (c2) {
                    const h2 = hub(c2)
                    addNode(h2, 'cluster', c2, { clusterIndex: 2, clusterFullId: c2 })
                    attachEntityList(h1, row.entity_list ?? row.entity_list1, 'c1')
                    attachEntityList(h2, row.entity_list2, 'c2')
                } else {
                    attachEntityList(h1, row.entity_list ?? row.entity_list1, 'c1')
                }
            })

            /* ── connecting_cluster_edges ──────────────────────────────────────────────
               Edges between the entities themselves: each record names one entity and the
               entities in the other cluster it is joined to. Both endpoints are resolved
               id → node so the line runs entity-to-entity, and because a record's targets are
               keyed by their own `entity_type~entity_domain` the line may cross type~domain
               groups (a `node~aqfer` entity joined to a `user~aqfer` one).

               The edge is bidirectional. A record's entity may sit in either cluster — and a shared
               id lists itself — so neither side is assumed: both endpoints resolve against every
               node, and only the cross-cluster pairs are joined. The line carries an arrowhead at
               both ends and both nodes report the relationship in their inspector. */
            /** Node pairs already joined, so a record listed from both ends draws one line. */
            const clusterEdgeLinkedNodes = new Set()
            /** Endpoints the payload calls connecting that sit in neither entity list — surfaced, not hidden. */
            let clusterEdgeUnresolved = 0
            /** Distinct id↔id joins, so the UI can say when one line stands for several. */
            const clusterEdgeIdPairs = new Set()
            if (clusterEdgeRecords.length) {
                /* `pairLabel|id` first so an id appearing under two type~domain keys resolves to
                   the right node; the bare id covers flat entity lists and collapsed bundles. */
                const nodeIdsByPairId = new Map()
                const nodeIdsById = new Map()
                const addLookup = (map, key, nodeId) => {
                    const prev = map.get(key)
                    if (prev) {
                        if (!prev.includes(nodeId)) prev.push(nodeId)
                    } else {
                        map.set(key, [nodeId])
                    }
                }
                for (const node of nodes) {
                    if (node.type === 'entity_group') {
                        const pl = String(node.neighborType || node.key || '')
                        for (const id of node.entityIds || []) {
                            addLookup(nodeIdsByPairId, `${pl}|${id}`, node.id)
                            addLookup(nodeIdsById, String(id), node.id)
                        }
                    } else if (node.type === 'entity') {
                        /* Flat entity lists only — grouped payloads never produce bare entity
                           nodes now that endpoints stay inside their split type~domain group. */
                        if (node.neighborType) {
                            addLookup(nodeIdsByPairId, `${String(node.neighborType)}|${node.key}`, node.id)
                        }
                        addLookup(nodeIdsById, String(node.key), node.id)
                    } else if (node.type === 'entity_bundle') {
                        for (const id of node.bundleHiddenAllIds || []) addLookup(nodeIdsById, String(id), node.id)
                    }
                }
                const nodeByIdC = new Map(nodes.map((n) => [n.id, n]))
                const sideOfNode = (nodeId) => {
                    const n = nodeByIdC.get(nodeId)
                    if (!n) return null
                    const k = n.nodeLinkKind || n.bundleLinkKind
                    return k === 'c1' || k === 'c2' ? k : null
                }
                const resolveIdToNodes = (pairLabel, id) =>
                    nodeIdsByPairId.get(`${pairLabel}|${id}`) || nodeIdsById.get(String(id)) || []
                /* nodeId → `${fromId}|${pairLabel}` → Set(ids), for the node inspector panel. */
                const edgeDetails = new Map()
                const noteDetail = (nodeId, fromId, pairLabel, toId) => {
                    let byKey = edgeDetails.get(nodeId)
                    if (!byKey) {
                        byKey = new Map()
                        edgeDetails.set(nodeId, byKey)
                    }
                    const k = `${fromId}|${pairLabel}`
                    let ids = byKey.get(k)
                    if (!ids) {
                        ids = new Set()
                        byKey.set(k, ids)
                    }
                    ids.add(String(toId))
                }
                /* Being joined is mutual, so a pair listed from both ends draws one line — flagged
                   bidirectional, which the renderer reads as an arrowhead at each end. */
                const undirectedPairKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`)
                const joinNodes = (aNodeId, bNodeId) => {
                    if (aNodeId === bNodeId) return
                    /* Only cross-cluster pairs: an edge inside one cluster is ordinary
                       membership structure, not what joins the two. */
                    const aSide = sideOfNode(aNodeId)
                    const bSide = sideOfNode(bNodeId)
                    if (aSide && bSide && aSide === bSide) return
                    const pk = undirectedPairKey(aNodeId, bNodeId)
                    if (clusterEdgeLinkedNodes.has(pk)) return
                    clusterEdgeLinkedNodes.add(pk)
                    pushEdge(aNodeId, bNodeId, 'cluster_edge', { bidirectional: true })
                }
                for (const rec of clusterEdgeRecords) {
                    const sourceNodeIds = resolveIdToNodes(rec.pairLabel, rec.entityId)
                    if (!sourceNodeIds.length) {
                        if (rec.targets.some((g) => g.ids.length)) clusterEdgeUnresolved += 1
                        continue
                    }
                    for (const grp of rec.targets) {
                        for (const targetId of grp.ids) {
                            const targetNodeIds = resolveIdToNodes(grp.pairLabel, targetId)
                            if (!targetNodeIds.length) {
                                clusterEdgeUnresolved += 1
                                continue
                            }
                            const a = `${rec.pairLabel}|${rec.entityId}`
                            const b = `${grp.pairLabel}|${targetId}`
                            clusterEdgeIdPairs.add(a < b ? `${a}::${b}` : `${b}::${a}`)
                            for (const srcNodeId of sourceNodeIds) {
                                for (const tgtNodeId of targetNodeIds) {
                                    /* Same node or same cluster: no line is drawn, so no detail
                                       either — else a shared id listing itself inside one cluster
                                       shows an edge count and a merge pill for a join that isn't. */
                                    if (srcNodeId === tgtNodeId) continue
                                    const sSide = sideOfNode(srcNodeId)
                                    const tSide = sideOfNode(tgtNodeId)
                                    if (sSide && tSide && sSide === tSide) continue
                                    noteDetail(srcNodeId, rec.entityId, grp.pairLabel, targetId)
                                    joinNodes(srcNodeId, tgtNodeId)
                                    /* Recorded from the far side too, so clicking either node
                                       reports the same relationship rather than only the side the
                                       column happened to be written from. */
                                    noteDetail(tgtNodeId, targetId, rec.pairLabel, rec.entityId)
                                }
                            }
                        }
                    }
                }
                for (const [nodeId, byKey] of edgeDetails) {
                    const node = nodeByIdC.get(nodeId)
                    if (!node) continue
                    const details = []
                    const sourceIds = new Set()
                    for (const [k, ids] of byKey) {
                        const sep = k.indexOf('|')
                        const fromId = k.slice(0, sep)
                        sourceIds.add(fromId)
                        details.push({ fromId, pairLabel: k.slice(sep + 1), ids: [...ids] })
                    }
                    node.hasClusterEdge = true
                    node.clusterEdgeDetails = details
                    node.clusterEdgeEntityIds = [...sourceIds]
                    node.clusterEdgeCount = details.reduce((sum, d) => sum + d.ids.length, 0)
                }
            }

            if (!nodes.length) return null
            /* Degree and per-cluster totals count membership edges only, so a cross-cluster edge
               never counts as membership of the cluster it merely joins. */
            const membershipEdges = edges.filter((e) => isMembershipLinkKind(e.linkKind))
            const deg = new Map()
            for (let ei = 0; ei < membershipEdges.length; ei++) {
                const e = membershipEdges[ei]
                deg.set(e.source, (deg.get(e.source) || 0) + 1)
                deg.set(e.target, (deg.get(e.target) || 0) + 1)
            }
            for (let ni = 0; ni < nodes.length; ni++) {
                const n = nodes[ni]
                if (n.type === 'cluster') {
                    n.hubConnectionCount = deg.get(n.id) || 0
                } else if (n.type === 'entity' && n.idCountInNode == null) {
                    n.idCountInNode = 1
                } else if (n.type === 'entity_group' && n.idCountInNode == null && n.entityIds) {
                    n.idCountInNode = n.entityIds.length
                }
            }
            // Sum idCountInNode of child nodes for each cluster's totalEntityIds
            const nodeById = new Map(nodes.map(n => [n.id, n]))
            for (let ei = 0; ei < membershipEdges.length; ei++) {
                const e = membershipEdges[ei]
                const src = nodeById.get(e.source)
                const tgt = nodeById.get(e.target)
                if (src && src.type === 'cluster' && tgt) {
                    src.totalEntityIds = (src.totalEntityIds || 0) + (tgt.idCountInNode || 0)
                }
                if (tgt && tgt.type === 'cluster' && src) {
                    tgt.totalEntityIds = (tgt.totalEntityIds || 0) + (src.idCountInNode || 0)
                }
            }
            return {
                nodes,
                edges,
                clusterEdgeStats: {
                    drawn: clusterEdgeLinkedNodes.size,
                    unresolved: clusterEdgeUnresolved,
                    /** id↔id joins the payload states, which one line can stand for several of. */
                    idPairs: clusterEdgeIdPairs.size,
                },
            }
        },
        /**
         * Builds Pail-style bracket neighbor strings from JSON rows (some serializers return arrays of objects).
         */
        neighborJsonArrayToPailBracketString(groups) {
            if (!Array.isArray(groups) || !groups.length) return ''
            const parts = []
            for (let i = 0; i < groups.length; i++) {
                const o = groups[i]
                if (!o || typeof o !== 'object') continue
                const keys = Object.keys(o)
                const typeKey   = keys.find(k => /type/i.test(k))
                const domainKey = keys.find(k => /domain/i.test(k))
                const idsKey    = keys.find(k => /ids?$/i.test(k))
                const t = typeKey   ? String(o[typeKey]   ?? '').trim() : ''
                const d = domainKey ? String(o[domainKey] ?? '').trim() : ''
                let ids = idsKey != null ? o[idsKey] : null
                if (ids == null) continue
                if (!Array.isArray(ids)) {
                    ids = String(ids)
                        .split(/[,|\s]+/)
                        .map((x) => x.trim())
                        .filter(Boolean)
                }
                const idStr = ids.map(String).filter(Boolean).join(', ')
                if (!t || !d || !idStr) continue
                parts.push(`{entity_neighbor_type=${t}, entity_neighbor_domain=${d}, ids=[${idStr}]}`)
            }
            if (!parts.length) return ''
            console.log('Converted neighbor JSON array to Pail bracket string:', parts)
            return `[${parts.join(', ')}]`
        },
        /**
         * Pail returns entity_list* either as bracket-strings (positional data[] rows) or as JSON arrays of structs.
         * Downstream parsing expects bracket-strings like production query results.
         */
        normalizePailEntityListField(raw) {
            if (raw == null || raw === '') return ''
            if (typeof raw === 'string') {
                const s = raw.trim()
                if (!s) return ''
                if (s.startsWith('[')) {
                    try {
                        const j = JSON.parse(s)
                        if (Array.isArray(j) && j.length && typeof j[0] === 'object' && j[0] !== null) {
                            if (Object.keys(j[0]).some(k => /type/i.test(k))) {
                                return this.neighborJsonArrayToPailBracketString(j)
                            }
                        }
                    } catch (_) {
                        /* not JSON — treat as Pail bracket-string or flat list */
                    }
                }
                return s
            }
            if (Array.isArray(raw)) {
                if (!raw.length) return ''
                if (typeof raw[0] === 'object' && raw[0] !== null && !Array.isArray(raw[0])) {
                    return this.neighborJsonArrayToPailBracketString(raw)
                }
                return raw.map(String).join(',')
            }
            if (typeof raw === 'object') {
                return this.neighborJsonArrayToPailBracketString([raw])
            }
            return String(raw).trim()
        },
        /**
         * Parses neighbor-group strings. Two API shapes are supported:
         *  - keyed:      [{entity_neighbor_type=type1, entity_neighbor_domain=domain3, ids=[9f5c209bff2e]}, ...]
         *  - positional: [[type1 domain1 [id1 id2 id3]] [type2 domain2 [id4 id5]]] (no keys, no commas anywhere)
         * entity_list1 / entity_list2 map to cluster hubs separately.
         * Neighbor rows are grouped into one graph node per neighborType~domain pair.
         * Each node uses neighborType as that combined label and entitySubgroups / entityIds for ids.
         */
        parseNeighborEntityGroups(raw) {
            const s = String(this.normalizePailEntityListField(raw) ?? '').trim()
            if (!s) return []
            if (/entity_(?:neighbor_)?type\s*=/i.test(s)) {
                return this.parseKeyedEntityGroups(s)
            }
            if (/\[\s*[^\s[\]]+\s+[^\s[\]]+\s+\[/.test(s)) {
                return this.parsePositionalEntityGroups(s)
            }
            return []
        },
        /** Keyed shape: {entity_type=.., entity_domain=.., ids=[..]} blocks, comma-separated. */
        parseKeyedEntityGroups(s) {
            let body = s
            if (body.startsWith('[')) body = body.slice(1).trim()
            if (body.endsWith(']')) body = body.slice(0, -1).trim()
            if (!body) return []
            const segments = body.split(/\}\s*,\s*\{/)
            const out = []
            for (let i = 0; i < segments.length; i++) {
                let block = segments[i].trim()
                if (!block.startsWith('{')) block = `{${block}`
                if (!block.endsWith('}')) block = `${block}}`
                // match both API shapes: entity_type / entity_neighbor_type, entity_domain / entity_neighbor_domain
                const typeM = block.match(/entity_(?:neighbor_)?type\s*=\s*([^,\s}]+)/)
                const domainM = block.match(/entity_(?:neighbor_)?domain\s*=\s*([^,\s}]+)/)
                const idsM = block.match(/ids\s*=\s*\[([^\]]*)\]/i)
                if (!typeM || !domainM || !idsM) continue
                const neighborType = String(typeM[1]).trim()
                const domain = String(domainM[1]).trim()
                const ids = String(idsM[1])
                    .split(/[,|\s]+/)
                    .map((x) => x.trim())
                    .filter(Boolean)
                if (ids.length) out.push({ neighborType, domain, ids })
            }
            return out
        },
        /**
         * Positional shape: [[type1 domain1 [id1 id2 ...]] [type2 domain2 [id3 ...]]] — no keys,
         * blocks and ids separated by whitespace only (no commas anywhere in the string).
         */
        parsePositionalEntityGroups(s) {
            const blockRe = /\[\s*([^\s[\]]+)\s+([^\s[\]]+)\s+\[([^\]]*)\]\s*\]/g
            const out = []
            let m
            while ((m = blockRe.exec(s)) !== null) {
                const neighborType = m[1].trim()
                const domain = m[2].trim()
                const ids = m[3]
                    .split(/\s+/)
                    .map((x) => x.trim())
                    .filter(Boolean)
                if (ids.length) out.push({ neighborType, domain, ids })
            }
            return out
        },
        parseEntityList(raw) {
            if (raw == null || raw === '') return []
            if (Array.isArray(raw)) {
                return raw.flatMap((x) => this.parseEntityList(x))
            }
            if (typeof raw === 'object') {
                const vals = Object.values(raw)
                if (vals.length && vals.every((v) => typeof v === 'string' || typeof v === 'number')) {
                    return vals.map(String)
                }
            }
            let s = String(raw).trim()
            if (!s) return []
            if (s.startsWith('[')) {
                try {
                    const j = JSON.parse(s)
                    return Array.isArray(j) ? j.map(String) : [String(j)]
                } catch (_) {
                    /* Pail array printed without quotes, e.g. "[id1, id2]" — drop the
                       delimiters so they don't stick to the first/last id. */
                    s = s.replace(/^\[/, '').replace(/\]$/, '').trim()
                    if (!s) return []
                }
            }
            return s.split(/[\s,|]+/).filter(Boolean)
        },
        rowsFromResult(result) {
            if (!result?.column_info || !result?.data) return []
            return result.data.map((row) => {
                const o = {}
                if (Array.isArray(row)) {
                    result.column_info.forEach((c, i) => {
                        o[c.name] = row[i]
                    })
                } else if (row && typeof row === 'object') {
                    result.column_info.forEach((c) => {
                        o[c.name] = row[c.name]
                    })
                }
                return o
            })
        },
        sleep(ms) {
            return new Promise((r) => setTimeout(r, ms))
        },
        async runPailQuery(query) {
            const cid = this.cid
            if (!cid) throw new Error('No client selected.')
            console.log('Running Pail query:', this.$store.state.pailUrl)
            const base = `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${cid}`
            const headers = {
                Authorization: `Bearer ${this.$store.state.accessToken}`,
                'Content-Type': 'application/json',
            }
            const post = await axios.post(`${base}/query_executions`, { query }, { headers })
            const qid = post.data?.query_execution_id
            if (!qid) throw new Error('No query_execution_id returned from Pail.')

            await this.sleep(800)

            for (let i = 0; i < MAX_POLL; i++) {
                const statusRes = await axios.get(`${base}/query_status/${qid}`, {
                    headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                })
                const st = statusRes.data
                if (st.State === 'FAILED') {
                    throw new Error(st.StateChangeReason || 'Query failed.')
                }
                if (st.State && st.State !== 'QUEUED' && st.State !== 'RUNNING') {
                    const res = await axios.get(`${base}/query_executions/${qid}`, {
                        headers: { Authorization: `Bearer ${this.$store.state.accessToken}` },
                    })
                    return res.data
                }
                await this.sleep(POLL_MS)
            }
            throw new Error('Query timed out.')
        },
        recommendationIdForPayload(row, idx) {
            if (row.recommendation_id != null && String(row.recommendation_id).trim() !== '') {
                return String(row.recommendation_id)
            }
            if (this.dataset === 'split') {
                return `${this.splitRowClusterId(row)}|${idx}`
            }
            return `${row.cluster_id1}|${row.cluster_id2}|${idx}`
        },
        curationEntityMap(pairs) {
            const byKey = new Map()
            for (const p of pairs) {
                const id = String(p.id ?? '').trim()
                if (!id) continue
                const key = String(p.pairLabel || '').trim() || CURATION_FLAT_PAIR_LABEL
                let set = byKey.get(key)
                if (!set) { set = new Set(); byKey.set(key, set) }
                set.add(id)
            }
            const out = {}
            for (const [key, set] of byKey) out[key] = [...set]
            return out
        },
        /** Flattens an entity_list field into `[{ pairLabel, id }]`. */
        curationMembershipPairs(raw) {
            if (raw == null || raw === '') return []
            const groups = this.parseNeighborEntityGroups(raw)
            if (groups.length) {
                const out = []
                for (const g of groups) {
                    const t = String(g.neighborType || '').trim() || 'unknown'
                    const d = String(g.domain || '').trim() || 'default'
                    const pairLabel = `${t}~${d}`
                    for (const id of g.ids || []) {
                        const sid = String(id).trim()
                        if (sid) out.push({ pairLabel, id: sid })
                    }
                }
                return out
            }
            const norm = this.normalizePailEntityListField(raw)
            if (!norm) return []
            return this.parseEntityList(norm)
                .map((id) => String(id).trim())
                .filter(Boolean)
                .map((id) => ({ pairLabel: CURATION_FLAT_PAIR_LABEL, id }))
        },
        /**
         * The detail row holding the entity lists for a recommendation. The list query returns
         * only ids, so membership comes from the cached detail query; the row itself is the
         * fallback when the graph for it was never opened.
         */
        curationDetailRowFor(row) {
            const rid =
                row.recommendation_id != null && String(row.recommendation_id).trim() !== ''
                    ? String(row.recommendation_id).trim()
                    : null
            const clusterId = String(row.cluster_id ?? row.cluster_id1 ?? '').trim()
            const cacheKey = rid
                ? this.cacheKeyForRecommendation(rid)
                : clusterId
                    ? `${this.dataset}::${clusterId}`
                    : null
            const entry = cacheKey ? this.detailCache[cacheKey] : null
            if (entry && entry.rows && entry.rows.length) return entry.rows[0]
            return row
        },
        /** Graph/split node id → real cluster id, for translating move records. */
        curationClusterIdByNodeId() {
            const map = new Map()
            const graph = this.detailGraph
            if (graph && graph.nodes) {
                for (const n of graph.nodes) {
                    if (n.type !== 'cluster') continue
                    const real = String(n.clusterFullId || n.key || '').trim()
                    if (real) map.set(n.id, real)
                }
            }
            /* Split clusters invented in the UI aren't in detailGraph until it re-renders. */
            for (const c of this.splitNewClusters) map.set(c.id, c.clusterId)
            return map
        },
        /**
         * `pairLabel` recorded on the move, falling back to stripping the trailing `~<id>` off
         * `nodeLabel` (which is built as `${neighborType}~${entityId}`). Splitting on '~' would
         * break on a domain or id containing one.
         */
        curationMovePairLabel(mv) {
            if (mv.pairLabel) return String(mv.pairLabel).trim()
            const label = String(mv.nodeLabel || '')
            const id = String(mv.entityId || '')
            const suffix = `~${id}`
            if (id && label.endsWith(suffix)) return label.slice(0, -suffix.length)
            return ''
        },
        /** `Map<"src\0tgt", pairs[]>` → `[{ source_cluster_id, target_cluster_id, entity_list }]`. */
        curationPairEntries(groups) {
            const out = []
            for (const [k, list] of groups) {
                const [source_cluster_id, target_cluster_id] = k.split('\0')
                const entity_list = this.curationEntityMap(list)
                if (!Object.keys(entity_list).length) continue
                out.push({ source_cluster_id, target_cluster_id, entity_list })
            }
            return out
        },
        /**
         * ACCEPT payload for split: final membership per cluster. Ids the user dragged onto a new
         * split cluster are attributed to that cluster's hash id, everything else stays with the
         * origin. splitNewClusters/splitNodeMoves only hold state for the row currently open in
         * the graph (reset on row switch — see resetNodeMoveState), so a row the user never
         * opened reports its original membership unchanged.
         */
        buildSplitCurationEntityLists(row) {
            const originId = String(row.cluster_id ?? row.cluster_id1 ?? '').trim()
            const detail = this.curationDetailRowFor(row)
            const pairs = this.curationMembershipPairs(detail.entity_list ?? detail.entity_list1)
            if (!pairs.length) return []

            const dest = new Map()
            if (originId && this.splitNewClusters.some((c) => c.originClusterId === originId)) {
                const hashByNodeId = new Map(this.splitNewClusters.map((c) => [c.id, c.clusterId]))
                for (const mv of this.splitNodeMoves) {
                    if (!mv.entityId) continue
                    const hash = hashByNodeId.get(mv.toClusterId)
                    if (hash) dest.set(String(mv.entityId), hash)
                }
            }

            const byCluster = new Map()
            for (const p of pairs) {
                const cid = dest.get(p.id) || originId
                if (!cid) continue
                let list = byCluster.get(cid)
                if (!list) { list = []; byCluster.set(cid, list) }
                list.push(p)
            }
            /* Origin first, then new clusters in creation order. `source_cluster_id` is the
               cluster the recommendation started from; on the retained entry it equals the
               target, which is how "these ids stay put" is expressed. */
            const order = [originId, ...this.splitNewClusters.map((c) => c.clusterId)]
            const ordered = new Map()
            for (const cid of order) {
                const list = byCluster.get(cid)
                if (!list || !list.length) continue
                ordered.set(`${originId}\0${cid}`, list)
                byCluster.delete(cid)
            }
            return this.curationPairEntries(ordered)
        },
        /**
         * ACCEPT payload for merge. The merge truthset is pair-level — one record per
         * `entity_lists` entry, not per entity — so each entry is a source → target move: the ids
         * that leave `source_cluster_id` for `target_cluster_id`. Listing a cluster's final
         * membership under a bare `target_cluster_id` (as the split payload does) would record no
         * source, which is why merge needs its own shape.
         *
         * With no drags at all, accepting means the recommendation stands as given: the whole of
         * cluster 2 folds into cluster 1.
         */
        buildMergeCurationEntityLists(row) {
            const c1 = String(row.cluster_id1 ?? '').trim()
            const c2 = String(row.cluster_id2 ?? '').trim()
            const detail = this.curationDetailRowFor(row)
            const sides = [
                { cid: c1, pairs: this.curationMembershipPairs(detail.entity_list ?? detail.entity_list1) },
                { cid: c2, pairs: this.curationMembershipPairs(detail.entity_list2) },
            ]

            /* Keyed by source cluster too: the same id string can legitimately appear in both
               clusters' lists, and a move only relocates the copy it was dragged from. */
            const real = this.curationClusterIdByNodeId()
            const moved = new Map()
            for (const mv of this.mergeNodeMoves) {
                if (!mv.entityId) continue
                const from = real.get(mv.fromClusterId)
                const to = real.get(mv.toClusterId)
                if (from && to && from !== to) moved.set(`${from}\0${String(mv.entityId)}`, to)
            }

            const groups = new Map()
            for (const side of sides) {
                if (!side.cid) continue
                for (const p of side.pairs) {
                    const to = moved.get(`${side.cid}\0${p.id}`)
                    if (!to) continue
                    const k = `${side.cid}\0${to}`
                    let list = groups.get(k)
                    if (!list) { list = []; groups.set(k, list) }
                    list.push(p)
                }
            }
            if (!groups.size && c1 && c2 && sides[1].pairs.length) {
                groups.set(`${c2}\0${c1}`, sides[1].pairs)
            }
            return this.curationPairEntries(groups)
        },
        /**
         * REJECT payload: the diff only — the entities the user actually moved out of their
         * original cluster, grouped by (source cluster → destination cluster). Anything left where
         * the recommendation put it is omitted. No comparison against the original membership is
         * needed: each move record already IS a delta, so the diff is just those records grouped.
         *
         * Rejecting without moving anything is the common case (the user simply disagrees with the
         * recommendation), and an empty `entity_lists` writes zero records — the decision would be
         * lost. So that falls back to the recommendation as it was given: for split, the cluster
         * stays whole (source = target = origin); for merge, cluster 2 does not join cluster 1.
         */
        buildRejectCurationEntityLists(row) {
            const real = this.curationClusterIdByNodeId()
            const detail = this.curationDetailRowFor(row)
            const groups = new Map()
            const add = (src, tgt, pair) => {
                if (!src || !tgt) return
                const k = `${src}\0${tgt}`
                let list = groups.get(k)
                if (!list) { list = []; groups.set(k, list) }
                list.push(pair)
            }
            if (this.dataset === 'split') {
                const originId = String(row.cluster_id ?? row.cluster_id1 ?? '').trim()
                for (const mv of this.splitNodeMoves) {
                    if (!mv.entityId) continue
                    add(originId, real.get(mv.toClusterId), {
                        pairLabel: this.curationMovePairLabel(mv),
                        id: mv.entityId,
                    })
                }
                if (!groups.size) {
                    const pairs = this.curationMembershipPairs(detail.entity_list ?? detail.entity_list1)
                    if (originId && pairs.length) groups.set(`${originId}\0${originId}`, pairs)
                }
            } else {
                for (const mv of this.mergeNodeMoves) {
                    if (!mv.entityId) continue
                    add(real.get(mv.fromClusterId), real.get(mv.toClusterId), {
                        pairLabel: this.curationMovePairLabel(mv),
                        id: mv.entityId,
                    })
                }
                if (!groups.size) {
                    const c1 = String(row.cluster_id1 ?? '').trim()
                    const c2 = String(row.cluster_id2 ?? '').trim()
                    const pairs = this.curationMembershipPairs(detail.entity_list2)
                    if (c1 && c2 && pairs.length) groups.set(`${c2}\0${c1}`, pairs)
                }
            }
            return this.curationPairEntries(groups)
        },
        buildSubmitPayload() {
            const curations = []
            this.recommendations.forEach((row, idx) => {
                const key = this.rowKey(row, idx)
                const decision = this.decisions[key]
                if (!decision) return
                const isReject = decision === 'reject'

                /* `accept` / `reject` decide which truthset the curation is recorded in.
                   recommendation_id and operation are optional, but kept so the service can tell
                   which recommendation a curation answers. */
                const curation = {
                    recommendation_id: this.recommendationIdForPayload(row, idx),
                    decision: isReject ? 'reject' : 'accept',
                    operation: 'physical',
                    entity_lists: isReject
                        ? this.buildRejectCurationEntityLists(row)
                        : this.dataset === 'split'
                            ? this.buildSplitCurationEntityLists(row)
                            : this.buildMergeCurationEntityLists(row),
                }
                curations.push(curation)
            })
            return { curations }
        },
        // ── Node movement helpers ──────────────────────────────────────
        resetNodeMoveState() {
            /* An Undo from the previous recommendation must not touch moves staged on this one. */
            this._dismissMergeToast()
            this._moveScope = (this._moveScope || 0) + 1
            this.mergeNodeMoves = []
            this.splitNewClusters = []
            this.splitNodeMoves = []
            this.splitSessionUUID = null
            this.moveSelectionNode = null
            this.moveSelectionIds = {}
            this.mergePreviewEnabled = false
        },

        /**
         * SHA-256 hex digest of a UTF-8 string.
         * Uses Web Crypto API when available (HTTPS / localhost), otherwise
         * falls back to a pure-JS implementation so it works on plain HTTP too.
         */
        async sha256hex(message) {
            if (
                typeof crypto !== 'undefined' &&
                crypto.subtle &&
                typeof crypto.subtle.digest === 'function'
            ) {
                const data = new TextEncoder().encode(message)
                const buffer = await crypto.subtle.digest('SHA-256', data)
                return Array.from(new Uint8Array(buffer))
                    .map(b => b.toString(16).padStart(2, '0'))
                    .join('')
            }
            // Fallback: synchronous pure-JS SHA-256 (FIPS 180-4)
            return this._sha256JS(message)
        },

        /** Pure-JS SHA-256 for environments where crypto.subtle is unavailable */
        _sha256JS(message) {
            const K = [
                0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
                0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
                0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
                0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
                0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
                0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
                0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
                0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
            ]
            const rotr = (x, n) => (x >>> n) | (x << (32 - n))

            // UTF-8 encode: unescape(encodeURIComponent()) is a well-known trick
            const utf8 = unescape(encodeURIComponent(message))
            const bytes = []
            for (let i = 0; i < utf8.length; i++) bytes.push(utf8.charCodeAt(i))

            // Pre-processing: padding
            const L = bytes.length
            bytes.push(0x80)
            while ((bytes.length % 64) !== 56) bytes.push(0)
            const bitLen = L * 8
            // Append bit-length as 64-bit big-endian (high 32 bits = 0 for < 512MB)
            bytes.push(0, 0, 0, 0)
            bytes.push((bitLen >>> 24) & 0xff, (bitLen >>> 16) & 0xff, (bitLen >>> 8) & 0xff, bitLen & 0xff)

            // Initial hash values
            let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a
            let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19

            for (let i = 0; i < bytes.length; i += 64) {
                const W = new Array(64)
                for (let j = 0; j < 16; j++) {
                    W[j] = (bytes[i + j * 4] << 24) | (bytes[i + j * 4 + 1] << 16) |
                           (bytes[i + j * 4 + 2] << 8) | bytes[i + j * 4 + 3]
                }
                for (let j = 16; j < 64; j++) {
                    const s0 = rotr(W[j - 15], 7) ^ rotr(W[j - 15], 18) ^ (W[j - 15] >>> 3)
                    const s1 = rotr(W[j - 2], 17) ^ rotr(W[j - 2], 19) ^ (W[j - 2] >>> 10)
                    W[j] = (W[j - 16] + s0 + W[j - 7] + s1) >>> 0
                }
                let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, hh = h7
                for (let j = 0; j < 64; j++) {
                    const S1  = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)
                    const ch  = (e & f) ^ (~e & g)
                    const t1  = (hh + S1 + ch + K[j] + W[j]) >>> 0
                    const S0  = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)
                    const maj = (a & b) ^ (a & c) ^ (b & c)
                    const t2  = (S0 + maj) >>> 0
                    hh = g; g = f; f = e; e = (d + t1) >>> 0
                    d = c; c = b; b = a; a = (t1 + t2) >>> 0
                }
                h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0
                h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + hh) >>> 0
            }
            return [h0, h1, h2, h3, h4, h5, h6, h7]
                .map(x => x.toString(16).padStart(8, '0')).join('')
        },

        ensureSplitSessionUUID() {
            if (!this.splitSessionUUID) {
                this.splitSessionUUID =
                    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                        ? crypto.randomUUID()
                        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
                              const r = (Math.random() * 16) | 0
                              return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
                          })
            }
            return this.splitSessionUUID
        },

        /** Create a new split cluster with SHA-256 name and add it to the graph */
        async createSplitCluster() {
            if (!this.selectedRow || this.dataset !== 'split') return
            const originClusterId = String(
                this.selectedRow.cluster_id ?? this.selectedRow.cluster_id1 ?? ''
            ).trim()
            if (!originClusterId) return

            const uuid = this.ensureSplitSessionUUID()
            const suffix = String(this.splitNewClusters.length + 2)  // 2, 3, 4 …
            const hashInput = originClusterId + suffix + uuid
            const hash = await this.sha256hex(hashInput)

            const newCluster = {
                id: `cluster:${hash}`,
                clusterId: hash,
                label: `New Cluster ${this.splitNewClusters.length + 1}`,
                originClusterId,
                index: this.splitNewClusters.length + 2,
                nodes: [],
            }
            this.splitNewClusters = [...this.splitNewClusters, newCluster]
        },

        /** Handler for 'node-moved' event emitted by D3CurationGraph */
        handleGraphNodeMoved({ nodeId, node, fromClusterId, toClusterId }) {
            if (!this.canCurate) return
            if (this.dataset === 'merge') {
                const before = this.mergeNodeMoves
                this._handleMergeNodeMove(nodeId, node, fromClusterId, toClusterId)
                this._announceMergeMove(before)
                // Hide preview merged as soon as the first move happens
                if (this.mergePreviewEnabled) this.mergePreviewEnabled = false
            } else if (this.dataset === 'split') {
                this._handleSplitNodeMove(nodeId, node, fromClusterId, toClusterId)
            }
            // Close the ID-selection panel after a successful drop
            if (this.moveSelectionNode && this.moveSelectionNode.nodeId === nodeId) {
                this.moveSelectionNode = null
                this.moveSelectionIds = {}
            }
        },

        /**
         * Returns human-readable label for a moved node.
         * entity_group: type~domain (group label — individual IDs expanded separately)
         * entity:       the entity ID
         * entity_bundle: "collapsed (+N)"
         */
        _nodeMoveLabel(node) {
            if (!node) return '—'
            if (node.type === 'entity_group') return node.neighborType || node.key || node.id
            if (node.type === 'entity_bundle') return `collapsed (+${node.bundleOverflow || '?'})`
            return node.key || node.id
        },

        /**
         * The ids a move of this node may name — the ones it actually owns.
         *
         * `node` is the node as drawn, and a group that received ids from a merge is drawn holding
         * them, so dragging it recorded a move of ids belonging to a different node. Those records
         * named an id the node never held, while the real owner's record still existed — one id
         * claimed by two moves going opposite ways, and the submit payload carrying both.
         */
        _ownedEntityIds(baseNodeId, node) {
            const drawn = this._getEntityGroupIds(node)
            const base = this.detailGraphFilteredForDisplay
            const owner = base && base.nodes && base.nodes.find((n) => n.id === baseNodeId)
            if (!owner) return drawn
            const owned = new Set(this._getEntityGroupIds(owner))
            return drawn.filter((id) => owned.has(id))
        },

        /** Extract the flat list of entity IDs from an entity_group node */
        _getEntityGroupIds(node) {
            if (!node) return []
            if (node.entityIds && node.entityIds.length) {
                return node.entityIds.map(String).filter(Boolean)
            }
            if (node.entitySubgroups && node.entitySubgroups.length) {
                const flat = node.entitySubgroups.flatMap(sg => (sg.entityIds || []).map(String))
                return [...new Set(flat)].filter(Boolean)
            }
            return []
        },

        /**
         * Buckets a group's ids by the whole set of type~domains their edges reach.
         *
         * Shared by the builder and by the post-move resolver, because the two must agree: the
         * resolver re-buckets from the edges that survive a merge, and if it bucketed differently
         * the fan-out would change shape for reasons that have nothing to do with the data.
         *
         * Bucketed by the whole signature, not one bucket per target, so every id appears in
         * exactly one sub-node. Per-target buckets would repeat an id in as many sub-nodes as it
         * has edges, and each sub-node offers a merge that moves its ids: the same id would then
         * be merged twice from two buttons that both looked correct.
         *
         * @returns {Array<{key: string, targets: string[], ids: string[]}>} widest relation first,
         *   the ids joined to nothing last. Empty when no member has an edge at all.
         */
        _bucketRelationGroups(entityIds, signatureOf) {
            const buckets = new Map()
            ;(entityIds || []).forEach((eid) => {
                const sig = signatureOf(eid)
                const targets = sig && sig.length ? sig : []
                const key = targets.length ? targets.join(' + ') : RELATION_NONE
                let b = buckets.get(key)
                if (!b) {
                    b = { key, targets, ids: [] }
                    buckets.set(key, b)
                }
                b.ids.push(eid)
            })
            /* Nothing to fan out when no member has an edge — one bucket holding everything. */
            if (buckets.size === 1 && buckets.has(RELATION_NONE)) return []
            if (!buckets.size) return []
            return [...buckets.values()].sort((a, b) => {
                if (!a.targets.length) return 1
                if (!b.targets.length) return -1
                return b.targets.length - a.targets.length || b.ids.length - a.ids.length ||
                    a.key.localeCompare(b.key)
            })
        },

        /**
         * A merge resolves the edges it merges.
         *
         * `clusterEdgeDetails` is loaded from the payload and says which ids joined the two
         * clusters *as loaded*. Once ids move, that is no longer what the graph shows: an edge
         * between a moved id and its far end has both ends in one cluster now, so it is not a
         * cluster edge any more. Leaving it recorded is what kept the rose ring, the group-count
         * chip and the fan-out on nodes whose connections had just been merged — on the moved node
         * and, less obviously, on the stationary node at the far end, which nothing had touched.
         *
         * Both ends are re-read from where the ids actually sit, so the far end is corrected too.
         * A no-op when nothing has moved: every far id is still on the other side.
         */
        _withResolvedClusterEdges(graph) {
            if (!graph || !graph.nodes || !graph.edges) return graph

            /* An id can legitimately sit on both sides — it appears in both entity lists — so the
               side is a set, not a value. */
            const sidesById = new Map()
            const sideOfNode = (n) => n.nodeLinkKind || n.bundleLinkKind || null
            for (const n of graph.nodes) {
                const side = sideOfNode(n)
                if (!side) continue
                for (const id of this._getEntityGroupIds(n)) {
                    if (!sidesById.has(id)) sidesById.set(id, new Set())
                    sidesById.get(id).add(side)
                }
                if (n.type === 'entity' && n.key) {
                    const k = String(n.key)
                    if (!sidesById.has(k)) sidesById.set(k, new Set())
                    sidesById.get(k).add(side)
                }
            }
            const sitsOpposite = (id, side) => {
                const sides = sidesById.get(String(id))
                if (!sides) return false
                for (const s of sides) if (s !== side) return true
                return false
            }

            const nodes = graph.nodes.map((n) => {
                if (n.type !== 'entity_group' || !n.hasClusterEdge) return n
                const side = sideOfNode(n)
                if (!side) return n

                const details = []
                for (const d of n.clusterEdgeDetails || []) {
                    const far = (d.ids || []).filter((id) => sitsOpposite(id, side))
                    if (far.length) details.push({ ...d, ids: far })
                }
                /* Which of this node's own ids still have a cross-cluster edge, and to what. */
                const targetsById = new Map()
                for (const d of details) {
                    const k = String(d.fromId)
                    if (!targetsById.has(k)) targetsById.set(k, new Set())
                    if (d.pairLabel) targetsById.get(k).add(String(d.pairLabel))
                }
                /* Re-bucketed, not filtered. Filtering dropped an id that lost its last edge out
                   of the fan entirely — including the "joined to nothing" slice, which is where it
                   now belongs and which is the only route left to merge it by hand. */
                const relationGroups = this._bucketRelationGroups(
                    this._getEntityGroupIds(n),
                    (id) => [...(targetsById.get(String(id)) || [])].sort()
                )

                return {
                    ...n,
                    relationGroups,
                    hasClusterEdge: details.length > 0,
                    clusterEdgeDetails: details,
                    clusterEdgeEntityIds: [...new Set(details.map((d) => String(d.fromId)))],
                    clusterEdgeCount: details.reduce((sum, d) => sum + d.ids.length, 0),
                }
            })

            /* The drawn lines follow the same rule: a cluster edge whose ends are now in the same
               cluster must not still be drawn between them. */
            const byId = new Map(nodes.map((n) => [n.id, n]))
            const isGroup = (n) => n && n.type === 'entity_group'
            const kept = graph.edges.filter((e) => {
                if (isMembershipLinkKind(e.linkKind)) return true
                const src = byId.get(e.source)
                const tgt = byId.get(e.target)
                if (!src || !tgt) return false
                /* Between two groups the line is re-derived below from the resolved details. */
                if (isGroup(src) && isGroup(tgt)) return false
                const ss = sideOfNode(src)
                const ts = sideOfNode(tgt)
                if (!ss || !ts) return true
                return ss !== ts
            })

            /* Group-to-group lines are rebuilt from the resolved details rather than filtered from
               the loaded ones. The loaded graph has one line per node pair standing for every id
               joined across it, and a move changes the nodes at its ends: the moved half is a new
               node, a folded one is gone, and the line had to be dropped for touching a moved node
               — taking the joins of every id that did not move with it. Re-reading which node
               holds each far id now draws exactly the pairs still joined. */
            const groupsById = new Map()
            for (const n of nodes) {
                if (!isGroup(n)) continue
                for (const id of this._getEntityGroupIds(n)) {
                    const k = String(id)
                    if (!groupsById.has(k)) groupsById.set(k, [])
                    groupsById.get(k).push(n)
                }
            }
            const joined = new Set()
            const rebuilt = []
            for (const a of nodes) {
                if (!isGroup(a) || !a.hasClusterEdge) continue
                const as = sideOfNode(a)
                for (const d of a.clusterEdgeDetails || []) {
                    for (const id of d.ids || []) {
                        for (const b of groupsById.get(String(id)) || []) {
                            if (b === a) continue
                            const bs = sideOfNode(b)
                            if (!as || !bs || as === bs) continue
                            /* The detail names the far group's type~domain; an id listed under
                               two groups on that side belongs to the one it names. */
                            if (d.pairLabel && b.neighborType && String(b.neighborType) !== String(d.pairLabel)) continue
                            const pk = a.id < b.id ? `${a.id}|${b.id}` : `${b.id}|${a.id}`
                            if (joined.has(pk)) continue
                            joined.add(pk)
                            rebuilt.push({ source: a.id, target: b.id, linkKind: 'cluster_edge', bidirectional: true })
                        }
                    }
                }
            }

            return { nodes, edges: [...kept, ...rebuilt] }
        },

        /**
         * Cluster-edge details for one half of a split entity_group node, so the remaining and the
         * moved halves each only claim the edges belonging to the ids they actually carry.
         * `clusterEdgeDetails` is keyed by the id the edge leaves from, which makes the narrowing
         * exact rather than a guess.
         */
        _clusterEdgeSubset(node, ids) {
            const keep = new Set(ids.map(String))
            /* The connection groups have to be narrowed with the edges, not left as loaded.
               They drive the rose "this one opens" ring, the group-count chip and whether the node
               fans out at all — so a half whose connected ids have moved away kept its ring and
               claimed groups holding ids it no longer had. */
            const groups = (node.relationGroups || [])
                .map((g) => ({ ...g, ids: (g.ids || []).filter((id) => keep.has(String(id))) }))
                .filter((g) => g.ids.length)
            /* Same invariant as buildRelationGroups: nothing to fan out when no surviving member
               has an edge, so the lone unlinked bucket collapses to no groups at all. */
            const relationGroups = groups.some((g) => (g.targets || []).length) ? groups : []

            if (!node.hasClusterEdge) return { relationGroups }
            const details = (node.clusterEdgeDetails || []).filter((d) => keep.has(String(d.fromId)))
            if (!details.length) {
                return {
                    relationGroups,
                    hasClusterEdge: false,
                    clusterEdgeDetails: [],
                    clusterEdgeEntityIds: [],
                    clusterEdgeCount: 0,
                }
            }
            return {
                relationGroups,
                hasClusterEdge: true,
                clusterEdgeDetails: details,
                clusterEdgeEntityIds: [...new Set(details.map((d) => String(d.fromId)))],
                clusterEdgeCount: details.reduce((sum, d) => sum + d.ids.length, 0),
            }
        },

        _handleMergeNodeMove(nodeId, node, fromClusterId, toClusterId) {
            // Partially/fully-moved entity_group nodes are re-rendered with a synthetic
            // "<id>|mv" id (see enhancedDisplayGraph). Move records are always keyed by
            // the canonical id, so resolve back to it before reading/writing them —
            // otherwise re-dragging an already-moved node creates an orphaned record
            // instead of updating the existing one.
            const baseNodeId = String(nodeId).endsWith('|mv') ? nodeId.slice(0, -3) : nodeId

            // Determine cluster labels from current graph
            const base = this.detailGraphFilteredForDisplay
            const fromCluster = base && base.nodes.find(n => n.id === fromClusterId)
            const toCluster   = base && base.nodes.find(n => n.id === toClusterId)
            const fromLabel = fromCluster ? `Cluster ${fromCluster.clusterIndex || '?'}` : fromClusterId
            const toLabel   = toCluster   ? `Cluster ${toCluster.clusterIndex   || '?'}` : toClusterId
            const nodeLabel = this._nodeMoveLabel(node)

            // Detect "move back to origin" — if any record for this nodeId has fromClusterId matching toClusterId
            const existingRec = this.mergeNodeMoves.find(m => m.nodeId === baseNodeId)
            if (existingRec && toClusterId === existingRec.fromClusterId) {
                this.mergeNodeMoves = this.mergeNodeMoves.filter(m => m.nodeId !== baseNodeId)
                return
            }

            // Build move records — entity_group expands to one row per entityId
            const moveBase = { fromClusterId, toClusterId, fromLabel, toLabel }
            let newEntries
            if (node.type === 'entity_group') {
                const neighborType = node.neighborType || node.key || 'group'
                let entityIds = this._ownedEntityIds(baseNodeId, node)
                // Apply ID selection filter if the panel is open for this node
                if (this.moveSelectionNode && this.moveSelectionNode.nodeId === nodeId && entityIds.length > 1) {
                    const selected = new Set(
                        Object.entries(this.moveSelectionIds).filter(([, on]) => on).map(([id]) => id)
                    )
                    if (selected.size > 0) entityIds = entityIds.filter(id => selected.has(id))
                }
                newEntries = entityIds.length > 0
                    ? entityIds.map(eid => ({
                          nodeId: baseNodeId, entityId: eid,
                          nodeLabel: `${neighborType}~${eid}`,
                          pairLabel: neighborType,
                          nodeType: 'entity_group', ...moveBase,
                      }))
                    : [{ nodeId: baseNodeId, entityId: null, nodeLabel, pairLabel: neighborType, nodeType: node.type, ...moveBase }]
            } else {
                // entity / entity_bundle: whole-node move
                newEntries = [{
                    nodeId: baseNodeId, entityId: node.key || null, nodeLabel,
                    pairLabel: node.neighborType || '', nodeType: node.type, ...moveBase,
                }]
            }

            // Replace all prior records for this nodeId, then append new ones
            this.mergeNodeMoves = [...this.mergeNodeMoves.filter(m => m.nodeId !== baseNodeId), ...newEntries]
        },

        _handleSplitNodeMove(nodeId, node, fromClusterId, toClusterId) {
            // Same canonical-id resolution as _handleMergeNodeMove — see comment there.
            const baseNodeId = String(nodeId).endsWith('|mv') ? nodeId.slice(0, -3) : nodeId
            const nodeLabel = this._nodeMoveLabel(node)

            // Resolve origin cluster (clusterIndex 1 in original graph)
            const originClusterNode = this.detailGraph &&
                this.detailGraph.nodes.find(n => n.type === 'cluster' && Number(n.clusterIndex) === 1)
            const originClusterId = originClusterNode ? originClusterNode.id : null

            // Moving back to origin = undo
            if (toClusterId === originClusterId) {
                this.splitNodeMoves = this.splitNodeMoves.filter(m => m.nodeId !== baseNodeId)
                this.splitNewClusters = this.splitNewClusters.map(c => ({
                    ...c, nodes: c.nodes.filter(id => id !== baseNodeId),
                }))
                return
            }

            const toClusterObj = this.splitNewClusters.find(c => c.id === toClusterId)
            if (!toClusterObj) return

            // Build entries with entityId
            let newMoveEntries
            if (node.type === 'entity_group') {
                const neighborType = node.neighborType || node.key || 'group'
                let entityIds = this._ownedEntityIds(baseNodeId, node)
                if (this.moveSelectionNode && this.moveSelectionNode.nodeId === nodeId && entityIds.length > 1) {
                    const selected = new Set(
                        Object.entries(this.moveSelectionIds).filter(([, on]) => on).map(([id]) => id)
                    )
                    if (selected.size > 0) entityIds = entityIds.filter(id => selected.has(id))
                }
                newMoveEntries = entityIds.length > 0
                    ? entityIds.map(eid => ({
                          nodeId: baseNodeId, entityId: eid,
                          nodeLabel: `${neighborType}~${eid}`,
                          // entity_list key in the submit payload — see curationMovePairLabel
                          pairLabel: neighborType,
                          nodeType: 'entity_group', toClusterId, toClusterLabel: toClusterObj.label,
                      }))
                    : [{ nodeId: baseNodeId, entityId: null, nodeLabel, pairLabel: neighborType, nodeType: node.type, toClusterId, toClusterLabel: toClusterObj.label }]
            } else {
                newMoveEntries = [{
                    nodeId: baseNodeId, entityId: node.key || null, nodeLabel,
                    pairLabel: node.neighborType || '', nodeType: node.type,
                    toClusterId, toClusterLabel: toClusterObj.label,
                }]
            }
            this.splitNodeMoves = [...this.splitNodeMoves.filter(m => m.nodeId !== baseNodeId), ...newMoveEntries]

            this.splitNewClusters = this.splitNewClusters.map(c => {
                if (c.id === toClusterId) return { ...c, nodes: [...c.nodes.filter(id => id !== baseNodeId), baseNodeId] }
                return { ...c, nodes: c.nodes.filter(id => id !== baseNodeId) }
            })
        },

        /**
         * For split-mode chip display: returns expanded labels for entity_group nodes
         * (one chip per entity ID as type~domain~entityId), or a single label otherwise.
         * Used by the split cluster section to render individual entity IDs.
         */
        getNodeLabelsById(nodeId) {
            const graph = this.enhancedDisplayGraph || this.detailGraphFilteredForDisplay
            if (!graph) return [nodeId]
            const n = graph.nodes.find(x => x.id === nodeId)
            if (!n) return [nodeId]
            if (n.type === 'entity_group') {
                const neighborType = n.neighborType || n.key || 'group'
                const ids = this._getEntityGroupIds(n)
                return ids.length > 0 ? ids.map(eid => `${neighborType}~${eid}`) : [neighborType]
            }
            return [this._nodeMoveLabel(n)]
        },

        getNodeLabelById(nodeId) {
            return this.getNodeLabelsById(nodeId).join(', ')
        },

        /**
         * Called when the user CLICKS an entity_group node in node-move mode.
         * Opens the floating ID selection panel. If the same node is clicked again,
         * the panel closes (toggle behaviour). Preserve existing selection when
         * clicking the same node so prior toggles are not lost.
         */
        handleGroupNodeClicked({ nodeId, node }) {
            const ids = this._getEntityGroupIds(node)
            if (ids.length <= 1) return  // single-ID groups move directly, no panel needed

            // Same node clicked again → dismiss (toggle off)
            if (this.moveSelectionNode && this.moveSelectionNode.nodeId === nodeId) {
                this.moveSelectionNode = null
                this.moveSelectionIds = {}
                return
            }

            // New node: open panel with all IDs checked by default
            const sel = {}
            ids.forEach(id => { sel[id] = true })
            this.moveSelectionNode = { nodeId, node }
            this.moveSelectionIds = sel
        },

        cancelMoveSelection() {
            this.moveSelectionNode = null
            this.moveSelectionIds = {}
        },

        /**
         * "Merge this connection": move just the ids of one fanned-out sub-node into the other
         * cluster. Toggles — a second call on the same sub-node puts those ids back.
         *
         * Deliberately not routed through `_handleMergeNodeMove`, which replaces every record for a
         * node id. Sub-nodes are slices of one parent, so merging a second connection group through
         * that path would silently undo the first. Here the records are keyed by (node id, entity
         * id) and only the ids named by this sub-node are touched, which is also what lets the
         * moves table and the submit payload stay exactly as they are — they already read one
         * record per entity id.
         */
        mergeConnectionGroup(spec) {
            if (!this.canCurate) return
            if (!spec || !spec.nodeId || !spec.node || !spec.entityIds || !spec.entityIds.length) return
            const ids = new Set(spec.entityIds.map(String))
            const baseNodeId = this._baseMergeNodeId(spec.nodeId)
            const already = this.mergeNodeMoves.filter(
                (m) => m.nodeId === baseNodeId && ids.has(String(m.entityId))
            ).length
            const before = this.mergeNodeMoves
            this._setConnectionGroupStaged(spec, already < ids.size)
            this._announceMergeMove(before)
        },

        dismissHint() {
            this.hintDismissed = true
            try { localStorage.setItem(HINT_DISMISSED_KEY, '1') } catch (e) { /* see readHintDismissed */ }
        },

        /**
         * Makes a staged merge visible. The move only lands in the review table, and on the canvas
         * the ids usually fold into the same type~domain group on the other side — so nothing on
         * screen moved, and curators could not tell where the id they clicked had gone. This rings
         * the node that received it and says so in a toast, with undo on click.
         *
         * `before` is the move list as it was; only moves it did not already hold are announced,
         * so un-staging (a toggle off, or a drag back to origin) stays silent.
         */
        _announceMergeMove(before) {
            const now = new Set(this.mergeNodeMoves.map(moveKey))
            const had = new Set(before.map(moveKey))
            const added = this.mergeNodeMoves.filter((m) => !had.has(moveKey(m)))
            const removed = before.filter((m) => !now.has(moveKey(m)))
            if (!added.length) {
                /* An undo by the ghost's pill, or a drag back to origin: confirm it, so the curator
                   is never left guessing whether it took. */
                if (removed.length) {
                    this.$store.dispatch('addToast', {
                        type: 'info',
                        message: `Undone: ${this._describeMovedIds(removed)} ${removed.length === 1 ? 'is' : 'are'} back in ${removed[0].fromLabel}.`,
                        config: { position: 'bottom-center', timeout: 4000 },
                    })
                }
                return
            }
            const keys = new Set(added.map(moveKey))

            const movedIds = new Set(added.map((m) => String(m.entityId)))
            const baseIds = new Set(added.map((m) => String(m.nodeId)))
            const graph = this.enhancedDisplayGraph
            const landed = (graph && graph.nodes ? graph.nodes : []).filter((n) =>
                (n.receivedEntityIds || []).some((id) => movedIds.has(String(id))) ||
                (n.isMoved && baseIds.has(String(n.id).replace(/\|mv$/, ''))) ||
                /* Whole-node moves keep their id and change side. */
                (baseIds.has(String(n.id)) && added.some((m) => m.entityId == null || m.nodeType !== 'entity_group'))
            ).map((n) => n.id)
            if (landed.length) this.mergeFlash = { ids: landed, token: Date.now() }

            const first = added[0]
            /* One merge toast at a time. Stacked, they read as several things happening at once,
               and an Undo on an older one looked like it touched the newest. Earlier moves stay
               undoable from their ghost's Undo pill and the Node Movements table. */
            this._dismissMergeToast()
            this._mergeToastId = `curation-merge-${Date.now()}`
            const scope = this._moveScope
            this.$store.dispatch('addToast', {
                type: 'success',
                message: {
                    component: UndoToast,
                    props: {
                        text: `Moved ${this._describeMovedIds(added)} from ${first.fromLabel} to ${first.toLabel}.`,
                    },
                    listeners: {
                        undo: () => {
                            if (scope === this._moveScope) this._undoMergeMove(before, keys)
                        },
                    },
                },
                config: { id: this._mergeToastId, position: 'bottom-center', timeout: 8000, closeOnClick: false },
            })
        },

        /** "em~sha256 ID …6a5e694e5a53", or "3 em~sha256 IDs" when several moved at once. */
        _describeMovedIds(moves) {
            const group = moves[0].pairLabel || 'group'
            if (moves.length === 1) {
                return moves[0].entityId != null ? `${group} ID ${shortId(moves[0].entityId)}` : group
            }
            return `${moves.length} ${group} IDs`
        },

        _dismissMergeToast() {
            if (this._mergeToastId) this.$store.dispatch('dismissToast', this._mergeToastId)
            this._mergeToastId = null
        },

        /** Reverts just the records one announced move added, leaving later moves alone. */
        _undoMergeMove(before, keys) {
            const undone = this.mergeNodeMoves.filter((m) => keys.has(moveKey(m)))
            this.mergeNodeMoves = [
                ...this.mergeNodeMoves.filter((m) => !keys.has(moveKey(m))),
                ...before.filter((m) => keys.has(moveKey(m))),
            ]
            if (undone.length) {
                this.$store.dispatch('addToast', {
                    type: 'info',
                    message: `Undone: ${this._describeMovedIds(undone)} ${undone.length === 1 ? 'is' : 'are'} back in ${undone[0].fromLabel}.`,
                    config: { position: 'bottom-center', timeout: 4000 },
                })
            }
        },

        _baseMergeNodeId(nodeId) {
            return String(nodeId).endsWith('|mv') ? String(nodeId).slice(0, -3) : String(nodeId)
        },

        /**
         * Stage or unstage one connection group, with the outcome named rather than toggled.
         *
         * The bulk merge needs this: driving it through the toggle would flip the groups that were
         * already staged back off, so a second click on "Merge all" would stage the untouched
         * groups and unstage the ones the user had merged by hand.
         */
        _setConnectionGroupStaged({ nodeId, node, entityIds, pairLabel }, staged) {
            if (!nodeId || !node || !entityIds || !entityIds.length) return
            const { target, source } = this.mergeHubNodes
            if (!target || !source) return
            const baseNodeId = this._baseMergeNodeId(nodeId)
            const side = node.nodeLinkKind || node.bundleLinkKind
            /* A group merges into the cluster on the other side of its own, whichever side that
               is — the connected ids are not always in cluster 2. */
            const fromHub = side === 'c2' ? source : target
            const toHub = side === 'c2' ? target : source
            const ids = new Set(entityIds.map(String))

            if (!staged) {
                this.mergeNodeMoves = this.mergeNodeMoves.filter(
                    (m) => !(m.nodeId === baseNodeId && ids.has(String(m.entityId)))
                )
                return
            }

            const groupLabel = node.neighborType || node.key || 'group'
            const moveBase = {
                fromClusterId: fromHub.id,
                toClusterId: toHub.id,
                fromLabel: `Cluster ${fromHub.clusterIndex || '?'}`,
                toLabel: `Cluster ${toHub.clusterIndex || '?'}`,
            }
            const entries = [...ids].map((eid) => ({
                nodeId: baseNodeId,
                entityId: eid,
                nodeLabel: `${groupLabel}~${eid}`,
                pairLabel: groupLabel,
                nodeType: 'entity_group',
                /* Which connection prompted the move, for the log. */
                viaConnection: pairLabel || '',
                ...moveBase,
            }))
            this.mergeNodeMoves = [
                ...this.mergeNodeMoves.filter((m) => !(m.nodeId === baseNodeId && ids.has(String(m.entityId)))),
                ...entries,
            ]
            if (this.mergePreviewEnabled) this.mergePreviewEnabled = false
        },

        /**
         * The connection groups on one side that actually have a cluster edge.
         *
         * Read off the untouched `detailGraph`, not the displayed one, so the set the button acts
         * on does not shrink as its own moves are staged. Groups with no targets are skipped: they
         * are the ids joined to nothing, and a bulk action named after connections must not drag
         * them along — they stay available to merge by hand.
         *
         * Bundles are excluded for the same reason the old button excluded them: a collapsed
         * bundle mixes connected with unconnected ids, so moving one wholesale moves ids that
         * have no edge to justify it.
         */
        _linkedGroupsOnSide(side) {
            const graph = this.detailGraph
            if (!graph || !graph.nodes) return []
            const out = []
            for (const n of graph.nodes) {
                if (n.type !== 'entity_group' || n.nodeLinkKind !== side) continue
                for (const grp of n.relationGroups || []) {
                    if (!grp.targets || !grp.targets.length) continue
                    if (!grp.ids || !grp.ids.length) continue
                    out.push({
                        nodeId: n.id,
                        node: n,
                        entityIds: grp.ids,
                        pairLabel: grp.key,
                    })
                }
            }
            return out
        },

        /**
         * Stage — or unstage — every connected id in one action, into cluster 1 by default.
         *
         * Not a loop over `mergeConnectionGroup`: that toggles per group, so a bulk call would
         * unstage whatever the user had already merged by hand. The outcome is decided once, from
         * `bulkMergeStaged`, and then applied to every group.
         */
        toggleBulkMerge() {
            const groups = this.bulkMergeGroups
            if (!groups.length) return
            const staged = !this.bulkMergeStaged
            for (const g of groups) this._setConnectionGroupStaged(g, staged)
        },

        /** Ids of one sub-node that are already staged to move — drives the button's merged state. */
        connectionGroupMergedIds(nodeId, entityIds) {
            const baseNodeId = String(nodeId || '').endsWith('|mv') ? nodeId.slice(0, -3) : nodeId
            const ids = new Set((entityIds || []).map(String))
            return this.mergeNodeMoves
                .filter((m) => m.nodeId === baseNodeId && ids.has(String(m.entityId)))
                .map((m) => String(m.entityId))
        },

        toggleMoveSelectionId(eid) {
            this.moveSelectionIds = { ...this.moveSelectionIds, [eid]: !this.moveSelectionIds[eid] }
        },

        selectAllMoveIds() {
            const sel = {}
            Object.keys(this.moveSelectionIds).forEach(k => { sel[k] = true })
            this.moveSelectionIds = sel
        },

        deselectAllMoveIds() {
            const sel = {}
            Object.keys(this.moveSelectionIds).forEach(k => { sel[k] = false })
            this.moveSelectionIds = sel
        },

        /**
         * Remove one specific entity ID from the merge migration.
         * If this was the last ID for that graph node, the visual override is also reverted.
         */
        removeMergeMove(nodeId, nodeLabel) {
            // Simply remove the entry — enhancedDisplayGraph recomputes automatically.
            // If no entries remain for nodeId, the node reverts to original cluster in the graph.
            this.mergeNodeMoves = this.mergeNodeMoves.filter(
                m => !(m.nodeId === nodeId && m.nodeLabel === nodeLabel)
            )
        },

        removeSplitMove(nodeId, nodeLabel) {
            this.splitNodeMoves = this.splitNodeMoves.filter(
                m => !(m.nodeId === nodeId && m.nodeLabel === nodeLabel)
            )
            // If no entries remain, remove the nodeId from cluster node lists too
            if (!this.splitNodeMoves.some(m => m.nodeId === nodeId)) {
                this.splitNewClusters = this.splitNewClusters.map(c => ({
                    ...c, nodes: c.nodes.filter(nid => nid !== nodeId),
                }))
            }
        },

        submitDecisions() {
            if (!this.canCurate || !this.canSaveDecisions || !this.cid) return
            const skipped = this.skippedRecommendationCount
            if (!skipped) return this.saveDecisions()
            const self = this
            this.$store.dispatch('showModal', {
                component: 'Confirm',
                data: {
                    title: 'Skip undecided recommendations?',
                    message:
                        `${skipped} recommendation${skipped === 1 ? '' : 's'} above your last decision ` +
                        `${skipped === 1 ? 'has' : 'have'} no decision and will be skipped — ` +
                        `${skipped === 1 ? 'it' : 'they'} won't be shown again. Save anyway?`,
                    confirmText: 'Save anyway',
                    confirm() {
                        return self.saveDecisions()
                    },
                },
            })
        },

        async saveDecisions() {
            if (!this.canCurate || !this.canSaveDecisions || !this.cid) return
            this.submitting = true
            const body = this.buildSubmitPayload()
            const nextOffset = this.computeNextCurationOffsetAfterSubmit()
            const url = `${this.$store.state.graphServiceUrl}/v1/cids/${encodeURIComponent(this.cid)}/curation/truthset?dataset=${this.dataset}`
            try {
                await axios.post(url, body, {
                    headers: {
                        Authorization: `Bearer ${this.$store.state.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                })
                this.$store.dispatch('addToast', {
                    type: 'success',
                    message: 'Decisions saved successfully.',
                })
                this.decisions = Object.create(null)
                if (nextOffset != null) {
                    try {
                        await this.setCurationOffset(nextOffset)
                        await this.refreshOffset()
                        await this.loadRecommendations({ skipOffsetRefresh: true })
                    } catch (offErr) {
                        const offMsg =
                            offErr.response?.data?.message ||
                            offErr.response?.data?.error ||
                            offErr.message ||
                            'Unknown error'
                        this.$store.dispatch('addToast', {
                            type: 'warning',
                            message: `Decisions were saved, but updating the offset or reloading the list failed (${offMsg}).`,
                            config: { timeout: 6000 },
                        })
                    }
                }
            } catch (e) {
                const status = e.response?.status
                const msg =
                    e.response?.data?.message ||
                    e.response?.data?.error ||
                    e.message ||
                    'Submit failed.'
                if (status === 404 || status === 405) {
                    this.$store.dispatch('addToast', {
                        type: 'warning',
                        message:
                            'Something went wrong while submitting the curation decisions. Please try again.',
                        config: { timeout: 5000 },
                    })
                } else {
                    this.$store.dispatch('addToast', { type: 'error', message: msg })
                }
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style scoped>
.graph-curation-page {
    --curation-list-rows: 8;
    --curation-table-row-h: 2.75rem;
    --curation-table-head-h: 2.5rem;
    --curation-table-max: calc(var(--curation-table-head-h) + var(--curation-list-rows) * var(--curation-table-row-h));
    /* Guarantees closure below the last card even if a child's margin-bottom
       collapses into this container's own — padding never collapses. */
    padding-bottom: 1.5rem;
}

/* ── Bench row: fill remaining viewport ─────────────────────────────────── */
@media (min-width: 992px) {
    .curation-bench-row {
        /* Fills the viewport normally, but grows past it once the graph has enough nodes to
           need the room — `--curation-bench-min-h` is bound from the node count. */
        height: max(calc(100vh - 9rem), var(--curation-bench-min-h, 620px));
        min-height: var(--curation-bench-min-h, 620px);
    }
    .curation-bench-row > .curation-bench-col {
        height: 100%;
        min-height: 0;
    }
    .curation-bench-row .recommendations-card,
    .curation-bench-row .graph-detail-card {
        height: 100%;
        min-height: 0;
    }
}

@media (max-width: 991.98px) {
    .curation-bench-row .recommendations-card {
        min-height: 420px;
    }
    .curation-bench-row .graph-detail-card {
        min-height: var(--curation-bench-min-h, 560px);
    }
}

.curation-bench-row > .curation-bench-col {
    min-height: 0;
}


.curation-list-body {
    min-height: 0;
    min-width: 0;
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    padding: 0.75rem 0.875rem 0.75rem 0.75rem !important;
}

.curation-hero {
    border-left: 4px solid #0d6efd;
    padding-left: 1rem;
    max-width: 52rem;
}

.recommendations-card-header {
    padding: 0.875rem 1.1rem 0.875rem 1.1rem;
    margin-bottom: 0;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.graph-detail-card-header {
    padding: 0.875rem 1.1rem 0.875rem 1.1rem;
    margin-bottom: 0;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* ── Unified topbar ──────────────────────────────────────────────────── */
.curation-topbar-card {
    margin-bottom: 1rem;
    background: #ffffff;
    border-left: 4px solid #0d6efd !important;
}

.curation-topbar-body {
    padding: 0.875rem 1.25rem;
}

.curation-topbar-inner {
    display: flex;
    align-items: center;
    gap: 0.5rem 0.75rem;
    flex-wrap: wrap;
    min-width: 0;
}

.curation-controls-group {
    display: flex;
    align-items: center;
    gap: 0.5rem 0.75rem;
    flex: 1 1 auto;
    min-width: 0;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.curation-topbar-sep {
    width: 1px;
    height: 2.25rem;
    background: rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    align-self: center;
    display: none;
}

@media (min-width: 1200px) {
    .curation-topbar-sep { display: block; }
}

.curation-stats-group {
    display: flex;
    align-items: center;
    gap: 0.5rem 0.75rem;
    flex-shrink: 0;
    flex-wrap: nowrap;
}

@media (max-width: 1199.98px) {
    .curation-topbar-inner { row-gap: 0.5rem; }
    .curation-controls-group { width: 100%; }
}

/* Inline field label */
.curation-inline-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6c757d;
    white-space: nowrap;
    flex-shrink: 0;
}

/* Vertical separator between toolbar groups */
.curation-v-sep {
    width: 1px;
    height: 1.75rem;
    background: rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    align-self: center;
}

/* Records number input */
.curation-records-input {
    width: 5rem;
    min-width: 3.5rem;
    max-width: 5.5rem;
}

.graph-detail-card {
    overflow: hidden;
}

.cluster-detail-panel {
    flex-shrink: 0;
    padding: 0.85rem 1rem 1rem;
    background: #f5f8fb;
    border-top: 2px solid #e3ebf6;
}

.cluster-detail-panel-empty {
    flex-shrink: 0;
    padding: 1.45rem 1.45rem 1.5rem;
    background: #f5f8fb;
    border-top: 2px solid #e3ebf6;
}

.cluster-detail-title {
    margin: 0 0 0.5rem;
    padding: 0;
    line-height: 1.35;
    font-size: 1.05rem;
    font-weight: 600;
    color: #212529;
}

.cluster-detail-summary-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
    margin-bottom: 0.6rem;
}

.cluster-pair-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    flex: 1 1 auto;
    min-width: min(100%, 10rem);
    padding: 0.45rem 0.65rem;
    margin-bottom: 0;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 0.375rem;
    color: #212529;
    line-height: 1.45;
}

.cluster-detail-summary-row .cluster-pair-bar {
    flex: 1 1 12rem;
    min-width: 0;
}

.cluster-pair-ids {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.65rem;
    min-width: 0;
}

.cluster-pair-id {
    word-break: break-word;
    overflow-wrap: anywhere;
}

.cluster-pair-sep {
    flex-shrink: 0;
    opacity: 0.55;
    font-weight: 500;
}

.cluster-section-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6c757d;
    margin-bottom: 0.4rem;
}

.cluster-section-label--inline {
    display: inline;
    margin-bottom: 0;
    margin-right: 0.35rem;
    line-height: 1.2;
    vertical-align: middle;
}

.cluster-anchor-row {
    margin-bottom: 1rem;
}

.cluster-anchor-row--inline {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.4rem;
    margin-bottom: 0;
    flex: 0 1 auto;
}

.bulk-merge-dir {
    font-weight: 600;
    opacity: 0.75;
    font-size: 0.92em;
}
.cluster-anchor-row--header {
    flex-shrink: 0;
    margin-bottom: 0;
}

.cluster-anchor-row .btn-anchor {
    background-image: none;
    font-weight: 500;
    line-height: 1.3;
    transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.cluster-anchor-row .btn-anchor-idle {
    color: #495057;
    background-color: #fff;
    border-color: #ced4da;
}

.cluster-anchor-row .btn-anchor-idle:hover {
    background-color: #f0f4ff;
    border-color: #93a8cc;
    color: #212529;
}

/* Both active = blue */
.cluster-anchor-row .btn-anchor-active {
    color: #fff;
    background-color: #0d6efd;
    border-color: #0d6efd;
    background-image: none;
}
.cluster-anchor-row .btn-anchor-active:hover {
    background-color: #0b5ed7;
    border-color: #0a58ca;
    color: #fff;
}

/* C1 active = blue */
.cluster-anchor-row .btn-anchor-c1.btn-anchor-active {
    background-color: #0d6efd;
    border-color: #0d6efd;
}
.cluster-anchor-row .btn-anchor-c1.btn-anchor-active:hover {
    background-color: #0b5ed7;
    border-color: #0a58ca;
}

/* C2 active = teal */
.cluster-anchor-row .btn-anchor-c2.btn-anchor-active {
    background-color: #0ca678;
    border-color: #0ca678;
}
.cluster-anchor-row .btn-anchor-c2.btn-anchor-active:hover {
    background-color: #0b9268;
    border-color: #0a8460;
}

.cluster-anchor-row .btn-anchor:focus {
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.28);
}

.cluster-query-block {
    margin: 0;
}

.cluster-detail-panel .cluster-query-snippet {
    max-height: min(calc(13rem * 1.45), calc(44vh * 1.45));
    overflow-y: auto;
}

.cluster-query-snippet {
    display: block;
    font-size: 0.75rem;
    line-height: 1.5;
    padding: 0.65rem 0.85rem;
    margin: 0;
    color: #212529;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 0.375rem;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}

/* Wrap SQL to width so the block stays readable without horizontal scroll */
.cluster-query-snippet--plain {
    white-space: normal;
    overflow-wrap: anywhere;
}

.graph-detail-card-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
    padding: 0.875rem 1rem 0.875rem 1rem !important;
}

.lead-small {
    font-size: 0.95rem;
}


/* Selected row: left accent + tinted background */
.curation-data-table tbody tr.table-active > td {
    background-color: #ebf2ff !important;
}
.curation-data-table tbody tr.table-active > td:first-child {
    box-shadow: inset 3px 0 0 #0d6efd;
}

.dataset-toggle .btn {
    min-width: 4.5rem;
}

.curation-data-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
}

.curation-th-checkbox,
.curation-td-checkbox {
    width: 2.35rem;
    padding-left: 0.35rem;
    padding-right: 0.35rem;
    vertical-align: middle;
}

.curation-bulk-bar {
    flex-shrink: 0;
    min-width: 0;
}

.cluster-link-btn {
    display: inline;
    max-width: 100%;
    padding: 0;
    margin: 0;
    border: none;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    line-height: inherit;
    text-decoration: none;
    word-break: break-word;
}

.cluster-link-btn:hover {
    color: #0d6efd;
    text-decoration: underline;
}

.cluster-link-btn.is-anchor-active {
    color: #0d6efd;
    font-weight: 600;
    text-decoration: none;
}

.cluster-link-btn.is-anchor-active:hover {
    text-decoration: underline;
}


/* Inline stat chip */
.curation-stat-chip {
    display: flex;
    flex-direction: column;
    gap: 0.08rem;
    min-width: 0;
    padding: 0.3rem 0.7rem;
    border-radius: 0.5rem;
    background: rgba(0, 0, 0, 0.035);
    border: 1px solid rgba(0, 0, 0, 0.055);
}

.curation-stat-chip__label {
    font-size: 0.6rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #8898aa;
    white-space: nowrap;
    line-height: 1.1;
}

.curation-stat-chip__value {
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
    white-space: nowrap;
}

.curation-stat-chip__value--decisions {
    color: #212529;
}

.graph-curation-page .btn-submit-review {
    background-image: none;
    background-color: #198754;
    border-color: #198754;
    color: #fff;
}

.graph-curation-page .btn-submit-review:hover:not(:disabled) {
    background-image: none;
    background-color: #157347;
    border-color: #146c43;
    color: #fff;
}

.graph-curation-page .btn-submit-review:focus {
    box-shadow: 0 0 0 0.2rem rgba(25, 135, 84, 0.35);
}

.curation-field {
    min-width: 8.5rem;
}

.curation-table-wrap {
    flex: 0 1 auto;
    max-height: var(--curation-table-max);
    min-height: 0;
    min-width: 0;
    overflow-x: hidden;
    overflow-y: auto;
    border-radius: 0.375rem;
    border: 1px solid rgba(0, 0, 0, 0.06);
    -webkit-overflow-scrolling: touch;
}

/* At desktop the bench is viewport-height; let the table grow to fill the card */
@media (min-width: 992px) {
    .curation-table-wrap {
        flex: 1 1 0;
        max-height: none;
    }
}

.curation-data-table {
    table-layout: fixed;
    width: 100%;
}

.curation-data-table .curation-col-w-check {
    width: 2.35rem;
}

.curation-data-table .curation-col-w-idx {
    width: 2.25rem;
}

.curation-data-table .curation-col-w-recid {
    width: 4rem;
}

.curation-data-table .curation-col-w-actions {
    width: 5.25rem;
}

.curation-data-table .curation-col-cluster,
.curation-data-table .curation-col-actions,
.curation-data-table .curation-col-actions-h {
    vertical-align: middle;
}

.curation-data-table .curation-col-cluster {
    overflow: hidden;
    max-width: 0;
}

.curation-col-actions {
    padding-left: 0.35rem;
    padding-right: 0.35rem;
}

.curation-action-btns .curation-decision-btn {
    width: 2.1rem;
    height: 1.8rem;
    padding: 0;
    font-size: 0.8rem;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    line-height: 1;
    transition: transform 0.1s, box-shadow 0.1s;
}
.curation-action-btns .curation-decision-btn:hover {
    transform: scale(1.08);
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

.cluster-link-btn--cell {
    display: block;
    width: 100%;
    max-width: 100%;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.curation-row {
    cursor: pointer;
}

/* Cluster ID sub-label inside anchor buttons */
.cluster-btn-id {
    display: block;
    font-size: 9px;
    opacity: 0.72;
    letter-spacing: 0.01em;
    max-width: 11rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1;
    margin-top: 1px;
}

.graph-detail-card .graph-detail-inner {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

/* cgraph-wrap inside canvas wrap fills all remaining height */
.graph-canvas-wrap :deep(.cgraph-wrap) {
    flex: 1 1 0;
    min-height: 0;
}

.graph-detail-card :deep(.cgraph-wrap) {
    flex: 1 1 0;
    min-height: 0 !important;
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.375rem;
    overflow: hidden;
}

/* Let the graph fill all remaining space in the card — no fixed min-height */
.graph-detail-card :deep(.cgraph-canvas) {
    flex: 1 1 0 !important;
    min-height: 0 !important;
    max-height: none;
}

@media (max-width: 991.98px) {
    .graph-detail-card :deep(.cgraph-canvas) {
        min-height: 360px !important;
    }
}

body.dark-mode .graph-detail-card :deep(.cgraph-wrap) {
    border-color: rgba(255, 255, 255, 0.1);
}

.selected-count{
    padding-left: 10px;
}
.font-monospace {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
}

body.dark-mode .curation-hero,

body.dark-mode .curation-topbar-card {
    background: #1a1d23;
}

body.dark-mode .curation-topbar-sep {
    background: rgba(255, 255, 255, 0.12);
}

body.dark-mode .curation-inline-label {
    color: #adb5bd;
}

body.dark-mode .curation-v-sep {
    background: rgba(255, 255, 255, 0.12);
}

body.dark-mode .curation-stat-chip__label {
    color: #adb5bd;
}

body.dark-mode .curation-stat-chip {
    background: rgba(255, 255, 255, 0.05);
}

body.dark-mode .curation-stat-chip__value--decisions {
    color: #e9ecef;
}

body.dark-mode .curation-stat-chip__value.text-primary {
    color: #6ea8fe !important;
}

body.dark-mode .graph-curation-page .btn-submit-review {
    background-image: none;
    background-color: #198754;
    border-color: #198754;
}

body.dark-mode .graph-curation-page .btn-submit-review:hover:not(:disabled) {
    background-image: none;
    background-color: #157347;
    border-color: #146c43;
}

body.dark-mode .cluster-link-btn:hover,
body.dark-mode .cluster-link-btn.is-anchor-active {
    color: #6ea8fe;
}

.curation-range-value {
    color: #212529;
    letter-spacing: 0.01em;
}

body.dark-mode .curation-range-value {
    color: #e9ecef;
}

/* Graph ID filter input */
.cluster-graph-toolbar {
    flex-shrink: 0;
    padding: 0.125rem 0;
}

.curation-graph-id-filter-input {
    max-width: 16rem;
}

/* The chip carries the line key now that the in-graph edge legend is gone: it draws the exact
   stroke of the lines it counts, in currentColor so dark mode follows for free. */
.chip-line-swatch {
    flex-shrink: 0;
    overflow: visible;
    margin-right: 0.125rem;
}

.cluster-edges-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
}

.cluster-edges-chip {
    font-size: 0.75rem;
    font-weight: 600;
    color: #be185d;
    background: rgba(190, 24, 93, 0.16);
    border-radius: 999px;
    padding: 0.125rem 0.5rem;
}

body.dark-mode .cluster-edges-chip {
    color: #fb7185;
}

body.dark-mode .curation-table-wrap {
    border-color: rgba(255, 255, 255, 0.08);
}

body.dark-mode .cluster-detail-panel,
body.dark-mode .cluster-detail-panel-empty {
    background: #20242c;
    border-top-color: rgba(255, 255, 255, 0.12);
}

body.dark-mode .cluster-detail-title {
    color: #e9ecef;
}

body.dark-mode .cluster-pair-bar,
body.dark-mode .cluster-query-snippet {
    background: #1e2128;
    border-color: rgba(255, 255, 255, 0.12);
    color: #e9ecef;
}

body.dark-mode .cluster-section-label {
    color: #adb5bd;
}

body.dark-mode .cluster-anchor-row .btn-anchor-idle {
    color: #dee2e6;
    background-color: #1e2128;
    border-color: rgba(255, 255, 255, 0.2);
}

body.dark-mode .cluster-anchor-row .btn-anchor-idle:hover {
    background-color: #2c313a;
    border-color: rgba(255, 255, 255, 0.28);
    color: #fff;
}

body.dark-mode .graph-detail-card-body {
    background: #1a1d23;
    padding: 0.875rem 1rem 0.875rem 1rem;
}

body.dark-mode .curation-data-table tbody tr.table-active > td {
    background-color: #1a2540 !important;
}

body.dark-mode .cluster-anchor-row .btn-anchor-c2.btn-anchor-active {
    background-color: #0a8a63;
    border-color: #0a8a63;
}

body.dark-mode .curation-table-wrap,
body.dark-mode .curation-data-table {
    --bs-table-hover-bg: rgba(255,255,255,0.04);
}

/* ── Node move hint bar ──────────────────────────────────────── */
.node-move-hint > span {
    min-width: 0;
}

.node-move-hint {
    flex-shrink: 0;
    min-width: 0;
    padding: 0.2rem 0.5rem;
    background: rgba(13, 110, 253, 0.05);
    border-radius: 0.375rem;
    border: 1px dashed rgba(13, 110, 253, 0.25);
}

/* ── Graph canvas wrapper (needed for overlay positioning) ────── */
.graph-canvas-wrap {
    flex: 1 1 0;
    min-height: 0;
    position: relative;
    display: flex;
    flex-direction: column;
}

/* ── Floating drag-selection panel ──────────────────────────── */
.drag-sel-panel {
    position: absolute;
    top: 0.75rem;
    left: 0.75rem;
    z-index: 30;
    width: min(320px, calc(100% - 1.5rem));

    /* Card-style — adapts to light/dark theme */
    background: #ffffff;
    border: 1px solid #E3EBF6;
    border-radius: 0.625rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.09), 0 1px 4px rgba(0, 0, 0, 0.05);

    display: flex;
    flex-direction: column;
    overflow: hidden;

    /* Panel background is transparent to pointer events so D3 drag can still
       start on any node sitting visually beneath the card. Interactive
       sections (header, chips, footer) re-enable pointer events individually. */
    pointer-events: none;
}
body.dark-mode .drag-sel-panel {
    background: #1e2128;
    border-color: #2e3238;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.25);
}

/* Header — re-enables pointer events */
.dsp-header {
    pointer-events: all;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.65rem 0.45rem;
    background: rgba(44, 123, 229, 0.04);
    border-bottom: 1px solid #E3EBF6;
}
body.dark-mode .dsp-header {
    background: rgba(44, 123, 229, 0.08);
    border-bottom-color: #2e3238;
}

.dsp-header-icon {
    font-size: 0.75rem;
    color: #2C7BE5;
    flex-shrink: 0;
}
body.dark-mode .dsp-header-icon { color: #6ea8fe; }

.dsp-type {
    font-family: ui-monospace, 'Cascadia Code', 'Fira Mono', monospace;
    font-size: 0.71rem;
    font-weight: 600;
    color: #12263F;
    letter-spacing: 0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 0;
}
body.dark-mode .dsp-type { color: #e1e3e8; }

/* Step badges */
.dsp-step-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    font-size: 0.6rem;
    font-weight: 800;
    padding: 0 5px;
    border-radius: 5px;
    background: rgba(44, 123, 229, 0.1);
    color: #2C7BE5;
    border: 1px solid rgba(44, 123, 229, 0.22);
    flex-shrink: 0;
}
body.dark-mode .dsp-step-badge {
    background: rgba(44, 123, 229, 0.2);
    color: #6ea8fe;
    border-color: rgba(44, 123, 229, 0.35);
}
.dsp-step-badge--2 {
    background: rgba(0, 217, 126, 0.08);
    color: #00875a;
    border-color: rgba(0, 217, 126, 0.22);
}
body.dark-mode .dsp-step-badge--2 {
    background: rgba(0, 217, 126, 0.15);
    color: #00D97E;
    border-color: rgba(0, 217, 126, 0.3);
}

/* Action buttons */
.dsp-actions {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    flex-shrink: 0;
}
.dsp-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: transparent;
    border: 1px solid #D2DDEC;
    border-radius: 4px;
    color: #6E84A3;
    font-size: 0.62rem;
    font-weight: 600;
    padding: 2px 7px;
    cursor: pointer;
    transition: background 0.12s, color 0.12s, border-color 0.12s;
    white-space: nowrap;
}
body.dark-mode .dsp-action-btn { border-color: #3a3f47; color: #a0a4b8; }
.dsp-action-btn i { font-size: 0.62rem; }
.dsp-action-btn:hover {
    background: rgba(44, 123, 229, 0.08);
    color: #2C7BE5;
    border-color: rgba(44, 123, 229, 0.3);
}
body.dark-mode .dsp-action-btn:hover {
    background: rgba(44, 123, 229, 0.15);
    color: #6ea8fe;
    border-color: rgba(44, 123, 229, 0.4);
}
.dsp-action-btn--clear:hover {
    background: rgba(110, 132, 163, 0.08);
    color: #3B506C;
    border-color: #B1C2D9;
}
body.dark-mode .dsp-action-btn--clear:hover {
    background: rgba(160, 164, 184, 0.08);
    color: #c5c8d6;
    border-color: #4a5060;
}
.dsp-close-btn {
    background: none;
    border: none;
    color: #B1C2D9;
    font-size: 0.82rem;
    cursor: pointer;
    padding: 2px 5px;
    border-radius: 4px;
    transition: color 0.12s, background 0.12s;
    line-height: 1;
    margin-left: 0.05rem;
}
body.dark-mode .dsp-close-btn { color: #4a5060; }
.dsp-close-btn:hover { color: #E63757; background: rgba(230, 55, 87, 0.08); }

/* Progress bar */
.dsp-progress-track {
    height: 2px;
    background: #EDF2F9;
    overflow: hidden;
}
body.dark-mode .dsp-progress-track { background: #252930; }
.dsp-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #2C7BE5 0%, #5ba0f5 100%);
    border-radius: 0 2px 2px 0;
    transition: width 0.25s ease;
    min-width: 3px;
}

/* Count subline */
.dsp-subline {
    padding: 0.3rem 0.65rem 0.28rem;
    font-size: 0.63rem;
    color: #6E84A3;
    display: flex;
    align-items: baseline;
    gap: 0.22rem;
    border-bottom: 1px solid #EDF2F9;
}
body.dark-mode .dsp-subline { color: #6E84A3; border-bottom-color: #2e3238; }
.dsp-sel-count {
    font-family: ui-monospace, monospace;
    font-size: 0.73rem;
    font-weight: 700;
    color: #2C7BE5;
}
body.dark-mode .dsp-sel-count { color: #6ea8fe; }
.dsp-sel-count--warn { color: #E63757 !important; }
.dsp-sel-sep { color: #B1C2D9; }
.dsp-sel-total {
    font-family: ui-monospace, monospace;
    font-size: 0.71rem;
    font-weight: 600;
    color: #95AAC9;
}

/* ID list — vertical rows, re-enables pointer events */
.dsp-chips-area {
    pointer-events: all;
    display: flex;
    flex-direction: column;
    max-height: 9rem;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: thin;
    scrollbar-color: #D2DDEC transparent;
    padding: 0.15rem 0;
}
body.dark-mode .dsp-chips-area { scrollbar-color: #2e3238 transparent; }
.dsp-chips-area::-webkit-scrollbar { width: 3px; }
.dsp-chips-area::-webkit-scrollbar-thumb { background: #D2DDEC; border-radius: 3px; }
body.dark-mode .dsp-chips-area::-webkit-scrollbar-thumb { background: #2e3238; }

/* Individual row */
.dsp-chip {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.32rem 0.65rem;
    width: 100%;
    border: none;
    border-radius: 0;
    border-left: 2px solid transparent;
    font-family: ui-monospace, 'Cascadia Code', 'Fira Mono', monospace;
    font-size: 0.7rem;
    cursor: pointer;
    transition: background 0.11s, border-color 0.11s;
    text-align: left;
    user-select: none;
    background: transparent;
}
.dsp-chip:active { opacity: 0.65; }

.dsp-chip--on {
    color: #12263F;
    border-left-color: #2C7BE5;
    background: rgba(44, 123, 229, 0.05);
}
body.dark-mode .dsp-chip--on {
    color: #e1e3e8;
    background: rgba(44, 123, 229, 0.1);
    border-left-color: #6ea8fe;
}
.dsp-chip--on:hover { background: rgba(44, 123, 229, 0.1); }
body.dark-mode .dsp-chip--on:hover { background: rgba(44, 123, 229, 0.17); }

.dsp-chip--off { color: #95AAC9; }
body.dark-mode .dsp-chip--off { color: #3f4550; }
.dsp-chip--off:hover {
    background: rgba(110, 132, 163, 0.06);
    color: #6E84A3;
    border-left-color: #D2DDEC;
}
body.dark-mode .dsp-chip--off:hover {
    background: rgba(255, 255, 255, 0.04);
    color: #a0a4b8;
    border-left-color: #3a3f47;
}

.dsp-chip-icon {
    font-size: 0.72rem;
    flex-shrink: 0;
    width: 13px;
    text-align: center;
}
.dsp-chip--on .dsp-chip-icon { color: #2C7BE5; }
body.dark-mode .dsp-chip--on .dsp-chip-icon { color: #6ea8fe; }
.dsp-chip--off .dsp-chip-icon { color: #D2DDEC; }
body.dark-mode .dsp-chip--off .dsp-chip-icon { color: #3a3f47; }

.dsp-chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 0;
    min-width: 0;
}

/* Footer — re-enables pointer events */
.dsp-footer {
    pointer-events: all;
    padding: 0.38rem 0.65rem 0.42rem;
    border-top: 1px solid #EDF2F9;
    font-size: 0.66rem;
    line-height: 1.4;
}
body.dark-mode .dsp-footer { border-top-color: #2e3238; }

.dsp-step2-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.dsp-warn-icon { color: #E63757; font-size: 0.7rem; }
.dsp-hint-icon { color: #2C7BE5; font-size: 0.7rem; }
body.dark-mode .dsp-hint-icon { color: #6ea8fe; }

.dsp-footer-hint { color: #6E84A3; }
body.dark-mode .dsp-footer-hint { color: #a0a4b8; }
.dsp-footer-hint strong { color: #12263F; font-weight: 700; }
body.dark-mode .dsp-footer-hint strong { color: #e1e3e8; }

.dsp-footer-warn { color: #E63757; }
body.dark-mode .dsp-footer-warn { color: #f5a0b5; }

/* Drop-in from top, quick fade-out */
.drag-sel-enter-active {
    transition: opacity 0.18s ease, transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.drag-sel-leave-active {
    transition: opacity 0.13s ease, transform 0.13s ease;
}
.drag-sel-enter-from, .drag-sel-leave-to {
    opacity: 0;
    transform: translateY(-8px) scale(0.97);
}

/* ── Node movement section (standalone card below the bench row) ───
   Top spacing lives here (no utility class — those carry !important
   and would win over this rule). Bottom spacing uses the mb-5 utility
   class directly on the element, so the page has clear closure below
   the card instead of ending flush against the viewport edge. */
.node-move-section { margin-top: 1.5rem; }

.node-move-card { border-left: 4px solid #0d6efd !important; }

.node-move-card-header {
    background: #f8fafc;
    border-bottom: 1px solid rgba(0,0,0,0.07);
    padding: 0.6rem 1rem;
}

.node-move-log-title {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #212529;
}

.node-move-clear-btn { font-size: 0.78rem; }

.curation-stat-chip__skipped {
    margin-left: 0.4rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: #b45309;
}
.curation-help-icon {
    font-size: 0.7em;
    opacity: 0.55;
    margin-left: 2px;
    cursor: help;
}

/* Merge table */
.node-move-table-scroll {
    max-height: 22rem;
    overflow-y: auto;
}

.node-move-table { font-size: 0.82rem; }

.node-move-id-text {
    font-family: ui-monospace, monospace;
    word-break: break-all;
}

.node-move-cluster-chip {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 0.74rem;
    font-weight: 600;
    white-space: nowrap;
}
.node-move-cluster-chip--from { background: #e9ecef; color: #495057; }
.node-move-cluster-chip--to   { background: #d1fae5; color: #065f46; }

/* Split sections */
.node-move-split-body {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 1rem;
    padding: 1rem;
    max-height: 26rem;
    overflow-y: auto;
}

.split-cluster-section {
    flex: 1 1 260px;
    background: #f8fafc;
    border: 1px solid rgba(0,0,0,0.07);
    border-radius: 0.5rem;
    padding: 0.65rem 0.875rem;
    border-left: 3px solid #0d6efd;
}

.split-cluster-section-header {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.split-cluster-log-label {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #212529;
}

.split-cluster-id {
    font-size: 0.68rem;
    opacity: 0.55;
    white-space: nowrap;
    overflow: hidden;
    max-width: 12rem;
    text-overflow: ellipsis;
    display: inline-block;
    vertical-align: middle;
}

.split-cluster-nodes {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
}

/* Removable chip wrapper for split mode */
.split-node-chip-wrap {
    display: inline-flex;
    align-items: center;
    background: #dbeafe;
    border-radius: 10px;
    font-size: 0.72rem;
    font-family: ui-monospace, monospace;
    overflow: hidden;
    max-width: 18rem;
}

.split-node-chip-label {
    padding: 2px 4px 2px 9px;
    color: #1e40af;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.split-chip-remove {
    background: none;
    border: none;
    color: #1e40af;
    cursor: pointer;
    padding: 2px 6px;
    line-height: 1;
    opacity: 0.55;
    font-size: 0.7rem;
    flex-shrink: 0;
    display: flex;
    align-items: center;
}

.split-chip-remove:hover {
    opacity: 1;
    color: #dc2626;
    background: rgba(220, 38, 38, 0.1);
}

/* Per-row remove button in the merge table */
.btn-remove-move {
    background: none;
    border: none;
    cursor: pointer;
    color: #adb5bd;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75rem;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    transition: color 0.12s, background 0.12s;
}

.btn-remove-move:hover {
    color: #dc2626;
    background: #fee2e2;
}

/* Slide-in transition for the whole section */
.nm-log-enter-active { transition: opacity 0.22s ease, transform 0.22s ease; }
.nm-log-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.nm-log-enter-from, .nm-log-leave-to { opacity: 0; transform: translateY(-8px); }

/* Dark mode */
body.dark-mode .node-move-hint {
    background: rgba(110, 168, 254, 0.06);
    border-color: rgba(110, 168, 254, 0.2);
}
body.dark-mode .node-move-card-header { background: #20242c; border-bottom-color: rgba(255,255,255,0.08); }
body.dark-mode .node-move-log-title   { color: #e9ecef; }
body.dark-mode .node-move-cluster-chip--from { background: rgba(255,255,255,0.08); color: #adb5bd; }
body.dark-mode .node-move-cluster-chip--to   { background: #052e16; color: #34d399; }
body.dark-mode .split-cluster-section   { background: #20242c; border-color: rgba(255,255,255,0.08); }
body.dark-mode .split-cluster-log-label { color: #e9ecef; }
body.dark-mode .split-node-chip-wrap    { background: #1e3a5f; }
body.dark-mode .split-node-chip-label   { color: #93c5fd; }
body.dark-mode .split-chip-remove       { color: #93c5fd; }
body.dark-mode .btn-remove-move         { color: #6c757d; }
body.dark-mode .btn-remove-move:hover   { color: #f87171; background: rgba(248, 113, 113, 0.12); }
</style>

<style>
/* Merge toast: rendered by vue-toastification outside this component, so not scoped. */
.curation-undo-toast {
    display: flex;
    align-items: center;
    gap: 12px;
}
.curation-undo-toast__btn {
    flex-shrink: 0;
    font-weight: 600;
}
</style>
